import { extendZodWithOpenApi } from '@asteasolutions/zod-to-openapi';
import { z } from 'zod';
export { z } from 'zod';

var __defProp = Object.defineProperty;
var __export = (target, all) => {
  for (var name in all)
    __defProp(target, name, { get: all[name], enumerable: true });
};

// src/common/index.ts
var common_exports = {};
__export(common_exports, {
  ErrorCode: () => ErrorCode,
  MAX_PAGE_SIZE: () => MAX_PAGE_SIZE,
  actorSchema: () => actorSchema,
  booleanQuerySchema: () => booleanQuerySchema,
  bulkIdsSchema: () => bulkIdsSchema,
  countResponseSchema: () => countResponseSchema,
  countryCodeSchema: () => countryCodeSchema,
  currencyCodeSchema: () => currencyCodeSchema,
  dateOnlySchema: () => dateOnlySchema,
  dateRangeQuerySchema: () => dateRangeQuerySchema,
  emailSchema: () => emailSchema,
  errorBodySchema: () => errorBodySchema,
  errorCodeSchema: () => errorCodeSchema,
  errorDetailSchema: () => errorDetailSchema,
  errorResponseSchema: () => errorResponseSchema,
  idParamSchema: () => idParamSchema,
  idSchema: () => idSchema,
  idempotencyKeySchema: () => idempotencyKeySchema,
  listQuerySchema: () => listQuerySchema,
  moneySchema: () => moneySchema,
  okEnvelope: () => okEnvelope,
  paginatedEnvelope: () => paginatedEnvelope,
  paginationMetaSchema: () => paginationMetaSchema,
  paginationQuerySchema: () => paginationQuerySchema,
  percentSchema: () => percentSchema,
  phoneSchema: () => phoneSchema,
  pincodeSchema: () => pincodeSchema,
  quantitySchema: () => quantitySchema,
  requestIdSchema: () => requestIdSchema,
  searchQuerySchema: () => searchQuerySchema,
  signedMoneySchema: () => signedMoneySchema,
  slugParamSchema: () => slugParamSchema,
  slugSchema: () => slugSchema,
  sortOrderSchema: () => sortOrderSchema,
  sortQuerySchema: () => sortQuerySchema,
  successResponseSchema: () => successResponseSchema,
  timestampSchema: () => timestampSchema,
  urlSchema: () => urlSchema,
  uuidSchema: () => uuidSchema,
  z: () => z
});
extendZodWithOpenApi(z);
z.config({
  customError: (issue) => {
    switch (issue.code) {
      case "invalid_type":
        return "This field is required";
      case "too_small":
        if (issue.origin === "string") {
          return issue.minimum === 1 ? "This field is required" : `Enter at least ${issue.minimum} characters`;
        }
        if (issue.origin === "number") return `Enter a value of at least ${issue.minimum}`;
        if (issue.origin === "array") return "Select at least one option";
        return void 0;
      case "too_big":
        if (issue.origin === "string") return `Enter at most ${issue.maximum} characters`;
        if (issue.origin === "number") return `Enter a value of at most ${issue.maximum}`;
        return void 0;
      case "invalid_format":
        if (issue.format === "uuid") return "Select an option";
        if (issue.format === "email") return "Enter a valid email address";
        if (issue.format === "url") return "Enter a valid URL";
        return "Enter a valid value";
      default:
        return void 0;
    }
  }
});

// src/common/index.ts
var uuidSchema = z.uuid("Select an option").openapi("Uuid");
var idSchema = z.coerce.number().int().positive().openapi("Id");
var slugSchema = z.string().trim().min(1, "Enter a slug").max(64).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "Use lowercase letters, numbers and hyphens only").openapi("Slug");
var timestampSchema = z.iso.datetime({ offset: true }).openapi("Timestamp");
var dateOnlySchema = z.iso.date().openapi("DateOnly");
var emailSchema = z.email("Enter a valid email address").max(254).openapi("Email");
var phoneSchema = z.string().trim().regex(/^\+?[0-9]{7,15}$/, "Enter a valid phone number").openapi("Phone");
var pincodeSchema = z.string().trim().regex(/^[A-Za-z0-9]{4,10}$/, "Enter a valid pincode").openapi("Pincode");
var countryCodeSchema = z.string().trim().regex(/^[A-Z]{2}$/, "Enter a valid country code").openapi("CountryCode");
var currencyCodeSchema = z.string().trim().regex(/^[A-Z]{3}$/).openapi("CurrencyCode");
var urlSchema = z.url().max(2048).openapi("Url");
var moneySchema = z.string().regex(/^\d{1,10}\.\d{2}$/).openapi("Money");
var signedMoneySchema = z.string().regex(/^-?\d{1,10}\.\d{2}$/).openapi("SignedMoney");
var percentSchema = z.number().min(0).max(100).openapi("Percent");
var quantitySchema = z.number().int().min(0).openapi("Quantity");
var idempotencyKeySchema = z.string().trim().min(8).max(128).openapi("IdempotencyKey");
var requestIdSchema = z.string().trim().min(1).max(64).openapi("RequestId");
var ErrorCode = {
  VALIDATION_ERROR: "VALIDATION_ERROR",
  UNAUTHORIZED: "UNAUTHORIZED",
  FORBIDDEN: "FORBIDDEN",
  NOT_FOUND: "NOT_FOUND",
  CONFLICT: "CONFLICT",
  RATE_LIMITED: "RATE_LIMITED",
  PAYMENT_FAILED: "PAYMENT_FAILED",
  INTERNAL_ERROR: "INTERNAL_ERROR"
};
var errorCodeSchema = z.enum(ErrorCode).openapi("ErrorCode");
var errorDetailSchema = z.object({ path: z.string(), message: z.string() }).openapi("ErrorDetail");
var errorBodySchema = z.object({
  code: errorCodeSchema,
  message: z.string(),
  details: z.array(errorDetailSchema).optional(),
  requestId: requestIdSchema
}).openapi("ErrorBody");
var errorResponseSchema = z.object({ error: errorBodySchema }).openapi("ErrorResponse");
var MAX_PAGE_SIZE = 100;
var paginationMetaSchema = z.object({
  page: z.number().int().min(1),
  limit: z.number().int().min(1).max(MAX_PAGE_SIZE),
  total: z.number().int().min(0),
  totalPages: z.number().int().min(0)
}).openapi("PaginationMeta");
var paginationQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(20)
}).openapi("PaginationQuery");
var sortOrderSchema = z.enum(["asc", "desc"]).openapi("SortOrder");
var sortQuerySchema = z.object({
  sort: z.string().trim().min(1).max(64).optional(),
  order: sortOrderSchema.default("desc")
}).openapi("SortQuery");
var searchQuerySchema = z.object({ search: z.string().trim().min(1).max(160).optional() }).openapi("SearchQuery");
var listQuerySchema = paginationQuerySchema.extend(sortQuerySchema.shape).extend(searchQuerySchema.shape).openapi("ListQuery");
var dateRangeQuerySchema = z.object({ dateFrom: timestampSchema.optional(), dateTo: timestampSchema.optional() }).openapi("DateRangeQuery");
var booleanQuerySchema = z.union([z.boolean(), z.stringbool()]).openapi("BooleanQuery");
var idParamSchema = z.object({ id: uuidSchema }).openapi("IdParam");
var slugParamSchema = z.object({ slug: slugSchema }).openapi("SlugParam");
var bulkIdsSchema = z.object({ ids: z.array(uuidSchema).min(1).max(100) }).openapi("BulkIds");
var actorSchema = z.object({ id: uuidSchema, name: z.string(), role: z.string() }).openapi("Actor");
var okEnvelope = (data) => z.object({ data });
var paginatedEnvelope = (item) => z.object({ data: z.array(item), meta: paginationMetaSchema });
var successResponseSchema = z.object({ data: z.object({ success: z.literal(true) }) }).openapi("SuccessResponse");
var countResponseSchema = z.object({ data: z.object({ count: z.number().int().min(0) }) }).openapi("CountResponse");

// src/auth/index.ts
var auth_exports = {};
__export(auth_exports, {
  AuthProvider: () => AuthProvider,
  PERMISSION_KEYS: () => PERMISSION_KEYS,
  UserRole: () => UserRole,
  UserStatus: () => UserStatus,
  accessTokenClaimsSchema: () => accessTokenClaimsSchema,
  adminSignupResponseSchema: () => adminSignupResponseSchema,
  adminSignupSchema: () => adminSignupSchema,
  authProviderSchema: () => authProviderSchema,
  authTokensSchema: () => authTokensSchema,
  deviceLabelSchema: () => deviceLabelSchema,
  googleIdentitySchema: () => googleIdentitySchema,
  googleLoginRequestSchema: () => googleLoginRequestSchema,
  googleRegisterRequestSchema: () => googleRegisterRequestSchema,
  loginResponseSchema: () => loginResponseSchema,
  loginResultSchema: () => loginResultSchema,
  logoutRequestSchema: () => logoutRequestSchema,
  otpAudienceSchema: () => otpAudienceSchema,
  otpEmailSchema: () => otpEmailSchema,
  otpRequestSchema: () => otpRequestSchema,
  otpVerifySchema: () => otpVerifySchema,
  permissionKeySchema: () => permissionKeySchema,
  profileUpdateSchema: () => profileUpdateSchema,
  refreshRequestSchema: () => refreshRequestSchema,
  refreshResponseSchema: () => refreshResponseSchema,
  registrationRequiredSchema: () => registrationRequiredSchema,
  sessionQuerySchema: () => sessionQuerySchema,
  sessionSchema: () => sessionSchema,
  sessionUserSchema: () => sessionUserSchema,
  userRoleSchema: () => userRoleSchema,
  userStatusSchema: () => userStatusSchema
});
var UserRole = {
  CUSTOMER: "customer",
  ADMIN: "admin",
  SUPER_ADMIN: "super_admin"
};
var userRoleSchema = z.enum(UserRole).openapi("UserRole");
var UserStatus = {
  ACTIVE: "active",
  INVITED: "invited",
  PENDING_APPROVAL: "pending_approval",
  REJECTED: "rejected",
  SUSPENDED: "suspended",
  DELETED: "deleted"
};
var userStatusSchema = z.enum(UserStatus).openapi("UserStatus");
var AuthProvider = { GOOGLE: "google", EMAIL: "email" };
var otpEmailSchema = emailSchema.openapi("OtpEmail");
var authProviderSchema = z.enum(AuthProvider).openapi("AuthProvider");
var PERMISSION_KEYS = [
  "catalog:read",
  "catalog:write",
  "inventory:read",
  "inventory:write",
  "vehicle:read",
  "vehicle:write",
  "vehicle_type:write",
  "fitment:write",
  "garage:manage",
  "cart:manage",
  "checkout:submit",
  "wishlist:manage",
  "order:create",
  "order:read_own",
  "order:read_all",
  "order:update",
  "order:transition",
  "order:cancel_own",
  "order:cancel_any",
  "payment:read",
  "refund:create",
  "shipment:read",
  "shipment:write",
  "customer:read",
  "customer:write",
  "promotion:read",
  "promotion:write",
  "media:upload",
  "media:delete",
  "report:read",
  "notification:read_own",
  "notification:send",
  "profile:manage",
  "settings:read",
  "settings:write",
  "admin_user:read",
  "admin_user:invite",
  "admin_user:write",
  "audit_log:read",
  "feedback:read"
];
var permissionKeySchema = z.enum(PERMISSION_KEYS).openapi("PermissionKey");
var sessionUserSchema = z.object({
  id: uuidSchema,
  email: emailSchema,
  name: z.string(),
  avatarUrl: urlSchema.nullable(),
  phone: phoneSchema.nullable(),
  role: userRoleSchema,
  status: userStatusSchema,
  permissions: z.array(permissionKeySchema),
  createdAt: timestampSchema
}).openapi("SessionUser");
var authTokensSchema = z.object({
  accessToken: z.string().min(1),
  refreshToken: z.string().min(1).optional(),
  tokenType: z.literal("Bearer"),
  expiresIn: z.number().int().positive()
}).openapi("AuthTokens");
var accessTokenClaimsSchema = z.object({
  sub: uuidSchema,
  email: emailSchema,
  role: userRoleSchema,
  sid: uuidSchema,
  iat: z.number().int(),
  exp: z.number().int()
}).openapi("AccessTokenClaims");
var deviceLabelSchema = z.string().trim().min(1).max(120).openapi("DeviceLabel");
var otpAudienceSchema = z.enum(["customer", "staff"]).openapi("OtpAudience");
var googleLoginRequestSchema = z.object({
  idToken: z.string().min(1),
  audience: otpAudienceSchema.optional(),
  deviceLabel: deviceLabelSchema.optional()
}).openapi("GoogleLoginRequest");
var googleRegisterRequestSchema = z.object({
  idToken: z.string().min(1),
  name: z.string().trim().min(1).max(120).optional(),
  phone: phoneSchema.optional(),
  acceptedTerms: z.literal(true),
  deviceLabel: deviceLabelSchema.optional()
}).openapi("GoogleRegisterRequest");
var googleIdentitySchema = z.object({
  email: emailSchema,
  name: z.string(),
  avatarUrl: urlSchema.nullable(),
  provider: authProviderSchema
}).openapi("GoogleIdentity");
var registrationRequiredSchema = z.object({ registrationRequired: z.literal(true), identity: googleIdentitySchema }).openapi("RegistrationRequired");
var loginResultSchema = z.object({ user: sessionUserSchema, tokens: authTokensSchema, isNewUser: z.boolean() }).openapi("LoginResult");
var loginResponseSchema = z.union([loginResultSchema, registrationRequiredSchema]).openapi("LoginResponse");
var refreshRequestSchema = z.object({ refreshToken: z.string().min(1).optional(), audience: otpAudienceSchema.optional() }).openapi("RefreshRequest");
var refreshResponseSchema = z.object({ tokens: authTokensSchema, user: sessionUserSchema }).openapi("RefreshResponse");
var logoutRequestSchema = z.object({
  refreshToken: z.string().min(1).optional(),
  audience: otpAudienceSchema.optional(),
  allDevices: z.boolean().default(false)
}).openapi("LogoutRequest");
var profileUpdateSchema = z.object({
  name: z.string().trim().min(1).max(120),
  phone: phoneSchema.nullable(),
  avatarUrl: urlSchema.nullable()
}).partial().openapi("ProfileUpdate");
var sessionSchema = z.object({
  id: uuidSchema,
  deviceLabel: z.string().nullable(),
  createdAt: timestampSchema,
  lastUsedAt: timestampSchema.nullable(),
  expiresAt: timestampSchema,
  revokedAt: timestampSchema.nullable(),
  current: z.boolean()
}).openapi("Session");
var sessionQuerySchema = listQuerySchema.extend({ active: booleanQuerySchema.optional() }).openapi("SessionQuery");
var otpRequestSchema = z.object({ email: otpEmailSchema }).openapi("OtpRequest");
var otpVerifySchema = z.object({
  email: otpEmailSchema,
  code: z.string().regex(/^\d{6}$/, "Enter the 6-digit code"),
  audience: otpAudienceSchema,
  deviceLabel: deviceLabelSchema.optional(),
  name: z.string().trim().min(1, "Enter your full name").max(120).optional()
}).openapi("OtpVerify");
var adminSignupSchema = z.object({
  email: otpEmailSchema,
  name: z.string().trim().min(1, "Enter your full name").max(120),
  phone: phoneSchema,
  requestedRole: z.enum(["admin"]).default("admin"),
  contactInfo: z.string().trim().max(500).optional()
}).openapi("AdminSignup");
var adminSignupResponseSchema = z.object({
  id: uuidSchema,
  email: emailSchema,
  name: z.string(),
  status: z.literal(UserStatus.PENDING_APPROVAL),
  message: z.string()
}).openapi("AdminSignupResponse");

// src/vehicles/index.ts
var vehicles_exports = {};
__export(vehicles_exports, {
  fitmentBulkCreateSchema: () => fitmentBulkCreateSchema,
  fitmentCheckRequestSchema: () => fitmentCheckRequestSchema,
  fitmentCheckResultSchema: () => fitmentCheckResultSchema,
  fitmentCreateSchema: () => fitmentCreateSchema,
  fitmentQuerySchema: () => fitmentQuerySchema,
  fitmentSchema: () => fitmentSchema,
  fitmentUpdateSchema: () => fitmentUpdateSchema,
  modelYearSchema: () => modelYearSchema,
  shopForVehicleSchema: () => shopForVehicleSchema,
  shopForVehicleSetSchema: () => shopForVehicleSetSchema,
  userVehicleCreateSchema: () => userVehicleCreateSchema,
  userVehicleQuerySchema: () => userVehicleQuerySchema,
  userVehicleSchema: () => userVehicleSchema,
  userVehicleUpdateSchema: () => userVehicleUpdateSchema,
  vehicleAttributesSchema: () => vehicleAttributesSchema,
  vehicleMakeCreateSchema: () => vehicleMakeCreateSchema,
  vehicleMakeQuerySchema: () => vehicleMakeQuerySchema,
  vehicleMakeSchema: () => vehicleMakeSchema,
  vehicleMakeUpdateSchema: () => vehicleMakeUpdateSchema,
  vehicleModelCreateSchema: () => vehicleModelCreateSchema,
  vehicleModelQuerySchema: () => vehicleModelQuerySchema,
  vehicleModelSchema: () => vehicleModelSchema,
  vehicleModelUpdateSchema: () => vehicleModelUpdateSchema,
  vehicleSelectionSchema: () => vehicleSelectionSchema,
  vehicleTypeCreateSchema: () => vehicleTypeCreateSchema,
  vehicleTypeQuerySchema: () => vehicleTypeQuerySchema,
  vehicleTypeSchema: () => vehicleTypeSchema,
  vehicleTypeUpdateSchema: () => vehicleTypeUpdateSchema,
  vehicleVariantCreateSchema: () => vehicleVariantCreateSchema,
  vehicleVariantQuerySchema: () => vehicleVariantQuerySchema,
  vehicleVariantSchema: () => vehicleVariantSchema,
  vehicleVariantUpdateSchema: () => vehicleVariantUpdateSchema
});
var vehicleAttributesSchema = z.record(z.string(), z.unknown()).openapi("VehicleAttributes");
var vehicleTypeSchema = z.object({
  id: uuidSchema,
  slug: slugSchema,
  name: z.string(),
  sortOrder: z.number().int(),
  isActive: z.boolean(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema
}).openapi("VehicleType");
var vehicleTypeCreateSchema = z.object({
  slug: slugSchema,
  name: z.string().trim().min(1).max(80),
  sortOrder: z.number().int().min(0).default(0),
  isActive: z.boolean().default(true)
}).openapi("VehicleTypeCreate");
var vehicleTypeUpdateSchema = z.object({
  slug: slugSchema,
  name: z.string().trim().min(1).max(80),
  sortOrder: z.number().int().min(0),
  isActive: z.boolean()
}).partial().openapi("VehicleTypeUpdate");
var vehicleTypeQuerySchema = listQuerySchema.extend({ isActive: booleanQuerySchema.optional() }).openapi("VehicleTypeQuery");
var vehicleMakeSchema = z.object({
  id: uuidSchema,
  vehicleTypeId: uuidSchema,
  vehicleTypeSlug: slugSchema,
  name: z.string(),
  slug: slugSchema,
  logoUrl: urlSchema.nullable(),
  sortOrder: z.number().int(),
  isActive: z.boolean(),
  modelCount: z.number().int().min(0).optional(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema
}).openapi("VehicleMake");
var vehicleMakeCreateSchema = z.object({
  vehicleTypeId: uuidSchema,
  name: z.string().trim().min(1).max(120),
  slug: slugSchema,
  logoUrl: urlSchema.nullable().optional(),
  sortOrder: z.number().int().min(0).default(0),
  isActive: z.boolean().default(true)
}).openapi("VehicleMakeCreate");
var vehicleMakeUpdateSchema = z.object({
  vehicleTypeId: uuidSchema,
  name: z.string().trim().min(1).max(120),
  slug: slugSchema,
  logoUrl: urlSchema.nullable(),
  sortOrder: z.number().int().min(0),
  isActive: z.boolean()
}).partial().openapi("VehicleMakeUpdate");
var vehicleMakeQuerySchema = listQuerySchema.extend({
  vehicleTypeId: uuidSchema.optional(),
  vehicleTypeSlug: slugSchema.optional(),
  isActive: booleanQuerySchema.optional()
}).openapi("VehicleMakeQuery");
var vehicleModelSchema = z.object({
  id: uuidSchema,
  makeId: uuidSchema,
  makeName: z.string(),
  vehicleTypeSlug: slugSchema,
  name: z.string(),
  slug: slugSchema,
  bodyType: z.string().nullable(),
  imageUrl: urlSchema.nullable(),
  isActive: z.boolean(),
  variantCount: z.number().int().min(0).optional(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema
}).openapi("VehicleModel");
var vehicleModelCreateSchema = z.object({
  makeId: uuidSchema,
  name: z.string().trim().min(1).max(120),
  slug: slugSchema,
  bodyType: z.string().trim().max(60).nullable().optional(),
  imageUrl: urlSchema.nullable().optional(),
  isActive: z.boolean().default(true)
}).openapi("VehicleModelCreate");
var vehicleModelUpdateSchema = z.object({
  makeId: uuidSchema,
  name: z.string().trim().min(1).max(120),
  slug: slugSchema,
  bodyType: z.string().trim().max(60).nullable(),
  imageUrl: urlSchema.nullable(),
  isActive: z.boolean()
}).partial().openapi("VehicleModelUpdate");
var vehicleModelQuerySchema = listQuerySchema.extend({
  makeId: uuidSchema.optional(),
  vehicleTypeId: uuidSchema.optional(),
  vehicleTypeSlug: slugSchema.optional(),
  bodyType: z.string().trim().max(60).optional(),
  isActive: booleanQuerySchema.optional()
}).openapi("VehicleModelQuery");
var modelYearSchema = z.number().int().min(1900).max(2200).openapi("ModelYear");
var vehicleVariantSchema = z.object({
  id: uuidSchema,
  modelId: uuidSchema,
  modelName: z.string(),
  makeName: z.string(),
  vehicleTypeSlug: slugSchema,
  name: z.string(),
  slug: slugSchema,
  yearFrom: modelYearSchema,
  yearTo: modelYearSchema.nullable(),
  attributes: vehicleAttributesSchema,
  isActive: z.boolean(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema
}).openapi("VehicleVariant");
var vehicleVariantCreateSchema = z.object({
  modelId: uuidSchema,
  name: z.string().trim().min(1).max(120),
  slug: slugSchema,
  yearFrom: modelYearSchema,
  yearTo: modelYearSchema.nullable().optional(),
  attributes: vehicleAttributesSchema.default({}),
  isActive: z.boolean().default(true)
}).refine((v) => v.yearTo == null || v.yearTo >= v.yearFrom, {
  path: ["yearTo"],
  message: "yearTo must not be earlier than yearFrom"
}).openapi("VehicleVariantCreate");
var vehicleVariantUpdateSchema = z.object({
  modelId: uuidSchema,
  name: z.string().trim().min(1).max(120),
  slug: slugSchema,
  yearFrom: modelYearSchema,
  yearTo: modelYearSchema.nullable(),
  attributes: vehicleAttributesSchema,
  isActive: z.boolean()
}).partial().openapi("VehicleVariantUpdate");
var vehicleVariantQuerySchema = listQuerySchema.extend({
  modelId: uuidSchema.optional(),
  makeId: uuidSchema.optional(),
  vehicleTypeSlug: slugSchema.optional(),
  year: z.coerce.number().int().min(1900).max(2200).optional(),
  isActive: booleanQuerySchema.optional()
}).openapi("VehicleVariantQuery");
var vehicleSelectionSchema = z.object({
  vehicleTypeSlug: slugSchema.optional(),
  makeId: uuidSchema.optional(),
  modelId: uuidSchema.optional(),
  variantId: uuidSchema.optional(),
  year: z.coerce.number().int().min(1900).max(2200).optional()
}).openapi("VehicleSelection");
var fitmentSchema = z.object({
  id: uuidSchema,
  productId: uuidSchema,
  vehicleTypeId: uuidSchema,
  makeId: uuidSchema.nullable(),
  makeName: z.string().nullable(),
  modelId: uuidSchema.nullable(),
  modelName: z.string().nullable(),
  variantId: uuidSchema.nullable(),
  variantName: z.string().nullable(),
  yearFrom: modelYearSchema.nullable(),
  yearTo: modelYearSchema.nullable(),
  note: z.string().nullable(),
  createdAt: timestampSchema
}).openapi("Fitment");
var fitmentCreateSchema = z.object({
  productId: uuidSchema,
  vehicleTypeId: uuidSchema,
  makeId: uuidSchema.nullable().optional(),
  modelId: uuidSchema.nullable().optional(),
  variantId: uuidSchema.nullable().optional(),
  yearFrom: modelYearSchema.nullable().optional(),
  yearTo: modelYearSchema.nullable().optional(),
  note: z.string().trim().max(240).nullable().optional()
}).refine((v) => v.modelId == null || v.makeId != null, {
  path: ["makeId"],
  message: "makeId is required when modelId is set"
}).refine((v) => v.variantId == null || v.modelId != null, {
  path: ["modelId"],
  message: "modelId is required when variantId is set"
}).openapi("FitmentCreate");
var fitmentUpdateSchema = z.object({
  vehicleTypeId: uuidSchema,
  makeId: uuidSchema.nullable(),
  modelId: uuidSchema.nullable(),
  variantId: uuidSchema.nullable(),
  yearFrom: modelYearSchema.nullable(),
  yearTo: modelYearSchema.nullable(),
  note: z.string().trim().max(240).nullable()
}).partial().openapi("FitmentUpdate");
var fitmentQuerySchema = listQuerySchema.extend(vehicleSelectionSchema.shape).extend({ productId: uuidSchema.optional() }).openapi("FitmentQuery");
var fitmentBulkCreateSchema = z.object({ productId: uuidSchema, fitments: z.array(fitmentCreateSchema).min(1).max(200) }).openapi("FitmentBulkCreate");
var fitmentCheckRequestSchema = z.object({ productIds: z.array(uuidSchema).min(1).max(100) }).extend(vehicleSelectionSchema.shape).openapi("FitmentCheckRequest");
var fitmentCheckResultSchema = z.object({
  productId: uuidSchema,
  fits: z.boolean(),
  matchedFitmentId: uuidSchema.nullable(),
  reason: z.string().nullable()
}).openapi("FitmentCheckResult");
var userVehicleSchema = z.object({
  id: uuidSchema,
  userId: uuidSchema,
  vehicleTypeId: uuidSchema,
  vehicleTypeSlug: slugSchema,
  makeId: uuidSchema,
  makeName: z.string(),
  modelId: uuidSchema,
  modelName: z.string(),
  variantId: uuidSchema.nullable(),
  variantName: z.string().nullable(),
  year: modelYearSchema.nullable(),
  nickname: z.string().nullable(),
  registrationNumber: z.string().nullable(),
  isPrimary: z.boolean(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema
}).openapi("UserVehicle");
var userVehicleCreateSchema = z.object({
  vehicleTypeId: uuidSchema,
  makeId: uuidSchema,
  modelId: uuidSchema,
  variantId: uuidSchema.nullable().optional(),
  year: modelYearSchema.nullable().optional(),
  nickname: z.string().trim().max(60).nullable().optional(),
  registrationNumber: z.string().trim().max(20).nullable().optional(),
  isPrimary: z.boolean().default(false)
}).openapi("UserVehicleCreate");
var userVehicleUpdateSchema = z.object({
  vehicleTypeId: uuidSchema,
  makeId: uuidSchema,
  modelId: uuidSchema,
  variantId: uuidSchema.nullable(),
  year: modelYearSchema.nullable(),
  nickname: z.string().trim().max(60).nullable(),
  registrationNumber: z.string().trim().max(20).nullable(),
  isPrimary: z.boolean()
}).partial().openapi("UserVehicleUpdate");
var userVehicleQuerySchema = listQuerySchema.extend({ userId: uuidSchema.optional(), vehicleTypeSlug: slugSchema.optional() }).openapi("UserVehicleQuery");
var shopForVehicleSchema = z.object({
  id: uuidSchema,
  title: z.string(),
  subtitle: z.string().nullable(),
  vehicleTypeId: uuidSchema.nullable(),
  makeId: uuidSchema,
  makeName: z.string(),
  modelId: uuidSchema,
  modelName: z.string(),
  variantId: uuidSchema.nullable(),
  variantName: z.string().nullable(),
  year: modelYearSchema.nullable(),
  userVehicleId: uuidSchema.nullable(),
  updatedAt: timestampSchema
}).openapi("ShopForVehicle");
var shopForVehicleSetSchema = z.object({
  vehicleTypeId: uuidSchema.nullable().optional(),
  makeId: uuidSchema,
  modelId: uuidSchema,
  variantId: uuidSchema.nullable().optional(),
  year: modelYearSchema.nullable().optional(),
  userVehicleId: uuidSchema.nullable().optional()
}).openapi("ShopForVehicleSet");

// src/catalog/index.ts
var catalog_exports = {};
__export(catalog_exports, {
  CrossRefType: () => CrossRefType,
  ProductCondition: () => ProductCondition,
  ProductGrade: () => ProductGrade,
  ProductReviewStatus: () => ProductReviewStatus,
  ProductStatus: () => ProductStatus,
  brandCreateSchema: () => brandCreateSchema,
  brandQuerySchema: () => brandQuerySchema,
  brandSchema: () => brandSchema,
  brandUpdateSchema: () => brandUpdateSchema,
  catalogFacetsSchema: () => catalogFacetsSchema,
  catalogSearchQuerySchema: () => catalogSearchQuerySchema,
  catalogSearchResultSchema: () => catalogSearchResultSchema,
  categoryCreateSchema: () => categoryCreateSchema,
  categoryQuerySchema: () => categoryQuerySchema,
  categoryReorderSchema: () => categoryReorderSchema,
  categorySchema: () => categorySchema,
  categoryTreeNodeSchema: () => categoryTreeNodeSchema,
  categoryUpdateSchema: () => categoryUpdateSchema,
  crossRefTypeSchema: () => crossRefTypeSchema,
  dimensionsSchema: () => dimensionsSchema,
  facetBucketSchema: () => facetBucketSchema,
  productCompatibilityQuerySchema: () => productCompatibilityQuerySchema,
  productConditionSchema: () => productConditionSchema,
  productCreateSchema: () => productCreateSchema,
  productCrossRefCreateSchema: () => productCrossRefCreateSchema,
  productCrossRefSchema: () => productCrossRefSchema,
  productCrossRefUpdateSchema: () => productCrossRefUpdateSchema,
  productGradeSchema: () => productGradeSchema,
  productListItemSchema: () => productListItemSchema,
  productMediaAddSchema: () => productMediaAddSchema,
  productMediaCreateSchema: () => productMediaCreateSchema,
  productMediaParamSchema: () => productMediaParamSchema,
  productMediaSchema: () => productMediaSchema,
  productMediaUpdateSchema: () => productMediaUpdateSchema,
  productQuerySchema: () => productQuerySchema,
  productReviewCreateSchema: () => productReviewCreateSchema,
  productReviewEligibilityQuerySchema: () => productReviewEligibilityQuerySchema,
  productReviewEligibilityReasonSchema: () => productReviewEligibilityReasonSchema,
  productReviewEligibilitySchema: () => productReviewEligibilitySchema,
  productReviewQuerySchema: () => productReviewQuerySchema,
  productReviewSchema: () => productReviewSchema,
  productReviewStatusSchema: () => productReviewStatusSchema,
  productReviewUpdateSchema: () => productReviewUpdateSchema,
  productSchema: () => productSchema,
  productSortSchema: () => productSortSchema,
  productStatusSchema: () => productStatusSchema,
  productSuggestionSchema: () => productSuggestionSchema,
  productSummarySchema: () => productSummarySchema,
  productUpdateSchema: () => productUpdateSchema,
  productVisualSearchQuerySchema: () => productVisualSearchQuerySchema
});

// src/media/index.ts
var media_exports = {};
__export(media_exports, {
  MediaFolder: () => MediaFolder,
  MediaKind: () => MediaKind,
  mediaAssetCreateSchema: () => mediaAssetCreateSchema,
  mediaAssetSchema: () => mediaAssetSchema,
  mediaAssetUpdateSchema: () => mediaAssetUpdateSchema,
  mediaDeleteRequestSchema: () => mediaDeleteRequestSchema,
  mediaFolderSchema: () => mediaFolderSchema,
  mediaKeySchema: () => mediaKeySchema,
  mediaKindSchema: () => mediaKindSchema,
  mediaQuerySchema: () => mediaQuerySchema,
  presignBatchRequestSchema: () => presignBatchRequestSchema,
  presignBatchResponseSchema: () => presignBatchResponseSchema,
  presignRequestSchema: () => presignRequestSchema,
  presignResponseSchema: () => presignResponseSchema,
  searchImagePresignRequestSchema: () => searchImagePresignRequestSchema
});
var MediaKind = {
  IMAGE: "image",
  VIDEO: "video",
  DOCUMENT: "document"
};
var mediaKindSchema = z.enum(MediaKind).openapi("MediaKind");
var MediaFolder = {
  PRODUCTS: "products",
  BRANDS: "brands",
  CATEGORIES: "categories",
  VEHICLES: "vehicles",
  SETTINGS: "settings",
  RETURNS: "returns",
  SEARCH: "search"
};
var mediaFolderSchema = z.enum(MediaFolder).openapi("MediaFolder");
var mediaKeySchema = z.string().trim().min(1).max(512).regex(/^[A-Za-z0-9!_.*'()\-/]+$/).openapi("MediaKey");
var presignRequestSchema = z.object({
  fileName: z.string().trim().min(1).max(255),
  contentType: z.string().trim().regex(/^[\w.+-]+\/[\w.+-]+$/),
  kind: mediaKindSchema,
  folder: mediaFolderSchema,
  sizeBytes: z.number().int().positive().max(50 * 1024 * 1024)
}).openapi("PresignRequest");
var presignResponseSchema = z.object({
  key: mediaKeySchema,
  uploadUrl: urlSchema,
  publicUrl: urlSchema,
  method: z.enum(["PUT", "POST"]),
  headers: z.record(z.string(), z.string()),
  fields: z.record(z.string(), z.string()).optional(),
  expiresAt: timestampSchema
}).openapi("PresignResponse");
var searchImagePresignRequestSchema = z.object({
  fileName: z.string().trim().min(1).max(255),
  contentType: z.string().trim().regex(/^image\/[\w.+-]+$/),
  sizeBytes: z.number().int().positive().max(10 * 1024 * 1024)
}).openapi("SearchImagePresignRequest");
var presignBatchRequestSchema = z.object({ files: z.array(presignRequestSchema).min(1).max(10) }).openapi("PresignBatchRequest");
var presignBatchResponseSchema = z.object({ uploads: z.array(presignResponseSchema) }).openapi("PresignBatchResponse");
var mediaAssetSchema = z.object({
  id: uuidSchema,
  key: mediaKeySchema,
  url: urlSchema,
  kind: mediaKindSchema,
  folder: mediaFolderSchema,
  contentType: z.string(),
  sizeBytes: z.number().int().min(0),
  width: z.number().int().positive().nullable(),
  height: z.number().int().positive().nullable(),
  createdAt: timestampSchema
}).openapi("MediaAsset");
var mediaAssetCreateSchema = z.object({
  key: mediaKeySchema,
  kind: mediaKindSchema,
  folder: mediaFolderSchema,
  contentType: z.string().trim().min(1).max(160),
  sizeBytes: z.number().int().min(0),
  width: z.number().int().positive().nullable().optional(),
  height: z.number().int().positive().nullable().optional()
}).openapi("MediaAssetCreate");
var mediaAssetUpdateSchema = z.object({
  kind: mediaKindSchema,
  folder: mediaFolderSchema,
  width: z.number().int().positive().nullable(),
  height: z.number().int().positive().nullable()
}).partial().openapi("MediaAssetUpdate");
var mediaQuerySchema = listQuerySchema.extend({ kind: mediaKindSchema.optional(), folder: mediaFolderSchema.optional() }).openapi("MediaQuery");
var mediaDeleteRequestSchema = z.object({ keys: z.array(mediaKeySchema).min(1).max(100) }).openapi("MediaDeleteRequest");

// src/catalog/index.ts
var ProductCondition = {
  NEW: "new",
  USED: "used"
};
var productConditionSchema = z.enum(ProductCondition).openapi("ProductCondition");
var ProductGrade = { A: "A", B: "B", C: "C" };
var productGradeSchema = z.enum(ProductGrade).openapi("ProductGrade");
var ProductStatus = {
  DRAFT: "draft",
  ACTIVE: "active",
  ARCHIVED: "archived"
};
var productStatusSchema = z.enum(ProductStatus).openapi("ProductStatus");
var CrossRefType = {
  OEM: "oem",
  AFTERMARKET: "aftermarket",
  ALTERNATE: "alternate"
};
var crossRefTypeSchema = z.enum(CrossRefType).openapi("CrossRefType");
var categorySchema = z.object({
  id: uuidSchema,
  parentId: uuidSchema.nullable(),
  name: z.string(),
  slug: slugSchema,
  description: z.string().nullable(),
  imageUrl: urlSchema.nullable(),
  sortOrder: z.number().int(),
  isActive: z.boolean(),
  showOnShopBy: z.boolean(),
  showOnHomeCategories: z.boolean(),
  productCount: z.number().int().min(0).optional(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema
}).openapi("Category");
var categoryTreeNodeSchema = categorySchema.extend({
  get children() {
    return z.array(categoryTreeNodeSchema);
  }
}).openapi("CategoryTreeNode");
var categoryCreateSchema = z.object({
  parentId: uuidSchema.nullable().optional(),
  name: z.string().trim().min(1).max(120),
  slug: slugSchema,
  description: z.string().trim().max(2e3).nullable().optional(),
  imageUrl: urlSchema.nullable().optional(),
  sortOrder: z.number().int().min(0).default(0),
  isActive: z.boolean().default(true),
  showOnShopBy: z.boolean().default(false),
  showOnHomeCategories: z.boolean().default(false)
}).openapi("CategoryCreate");
var categoryUpdateSchema = z.object({
  parentId: uuidSchema.nullable(),
  name: z.string().trim().min(1).max(120),
  slug: slugSchema,
  description: z.string().trim().max(2e3).nullable(),
  imageUrl: urlSchema.nullable(),
  sortOrder: z.number().int().min(0),
  isActive: z.boolean(),
  showOnShopBy: z.boolean(),
  showOnHomeCategories: z.boolean()
}).partial().openapi("CategoryUpdate");
var categoryQuerySchema = listQuerySchema.extend({
  parentId: uuidSchema.optional(),
  rootOnly: booleanQuerySchema.optional(),
  isActive: booleanQuerySchema.optional(),
  showOnShopBy: booleanQuerySchema.optional(),
  showOnHomeCategories: booleanQuerySchema.optional()
}).openapi("CategoryQuery");
var categoryReorderSchema = z.object({
  items: z.array(z.object({ id: uuidSchema, sortOrder: z.number().int().min(0) })).min(1)
}).openapi("CategoryReorder");
var brandSchema = z.object({
  id: uuidSchema,
  parentId: uuidSchema.nullable(),
  name: z.string(),
  slug: slugSchema,
  logoUrl: urlSchema.nullable(),
  description: z.string().nullable(),
  isActive: z.boolean(),
  productCount: z.number().int().min(0).optional(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema
}).openapi("Brand");
var brandCreateSchema = z.object({
  parentId: uuidSchema.nullable().optional(),
  name: z.string().trim().min(1).max(120),
  slug: slugSchema,
  logoUrl: urlSchema.nullable().optional(),
  description: z.string().trim().max(2e3).nullable().optional(),
  isActive: z.boolean().default(true)
}).openapi("BrandCreate");
var brandUpdateSchema = z.object({
  parentId: uuidSchema.nullable(),
  name: z.string().trim().min(1).max(120),
  slug: slugSchema,
  logoUrl: urlSchema.nullable(),
  description: z.string().trim().max(2e3).nullable(),
  isActive: z.boolean()
}).partial().openapi("BrandUpdate");
var brandQuerySchema = listQuerySchema.extend({ parentId: uuidSchema.optional(), isActive: booleanQuerySchema.optional() }).openapi("BrandQuery");
var productMediaSchema = z.object({
  id: uuidSchema,
  productId: uuidSchema,
  key: mediaKeySchema,
  url: urlSchema,
  kind: mediaKindSchema,
  altText: z.string().nullable(),
  sortOrder: z.number().int(),
  createdAt: timestampSchema
}).openapi("ProductMedia");
var productMediaCreateSchema = z.object({
  key: mediaKeySchema,
  kind: mediaKindSchema.default("image"),
  altText: z.string().trim().max(200).nullable().optional(),
  sortOrder: z.number().int().min(0).default(0)
}).openapi("ProductMediaCreate");
var productMediaAddSchema = z.object({ media: z.array(productMediaCreateSchema).min(1).max(5) }).openapi("ProductMediaAdd");
var productMediaParamSchema = z.object({ id: uuidSchema, mediaId: uuidSchema }).openapi("ProductMediaParam");
var productMediaUpdateSchema = z.object({
  altText: z.string().trim().max(200).nullable(),
  sortOrder: z.number().int().min(0)
}).partial().openapi("ProductMediaUpdate");
var productCrossRefSchema = z.object({
  id: uuidSchema,
  productId: uuidSchema,
  type: crossRefTypeSchema,
  number: z.string(),
  brandName: z.string().nullable(),
  createdAt: timestampSchema
}).openapi("ProductCrossRef");
var productCrossRefCreateSchema = z.object({
  type: crossRefTypeSchema,
  number: z.string().trim().min(1).max(80),
  brandName: z.string().trim().max(120).nullable().optional()
}).openapi("ProductCrossRefCreate");
var productCrossRefUpdateSchema = z.object({
  type: crossRefTypeSchema,
  number: z.string().trim().min(1).max(80),
  brandName: z.string().trim().max(120).nullable()
}).partial().openapi("ProductCrossRefUpdate");
var dimensionsSchema = z.object({
  lengthMm: z.number().int().positive(),
  widthMm: z.number().int().positive(),
  heightMm: z.number().int().positive()
}).openapi("Dimensions");
var productSummarySchema = z.object({
  id: uuidSchema,
  title: z.string(),
  slug: slugSchema,
  sku: z.string(),
  imageUrl: urlSchema.nullable(),
  price: moneySchema,
  mrp: moneySchema.nullable(),
  specialOfferPrice: moneySchema.nullable(),
  condition: productConditionSchema,
  status: productStatusSchema
}).openapi("ProductSummary");
var productListItemSchema = productSummarySchema.extend({
  brandId: uuidSchema.nullable(),
  brandName: z.string().nullable(),
  categoryId: uuidSchema,
  categoryName: z.string(),
  vehicleMake: z.string().nullable(),
  vehicleModel: z.string().nullable(),
  vehicleVariant: z.string().nullable(),
  vehicleYear: modelYearSchema.nullable(),
  oemNumber: z.string().nullable(),
  grade: productGradeSchema.nullable(),
  quantity: z.number().int().min(0),
  isInStock: z.boolean(),
  isFeatured: z.boolean(),
  reviewAverage: z.number().min(1).max(5).nullable(),
  reviewCount: z.number().int().min(0),
  updatedAt: timestampSchema
}).openapi("ProductListItem");
var productSchema = productListItemSchema.extend({
  description: z.string().nullable(),
  warrantyDays: z.number().int().min(0).nullable(),
  weightGrams: z.number().int().min(0),
  dimensions: dimensionsSchema.nullable(),
  hsnCode: z.string().nullable(),
  taxRate: percentSchema,
  media: z.array(productMediaSchema),
  crossRefs: z.array(productCrossRefSchema),
  fitmentCount: z.number().int().min(0),
  createdAt: timestampSchema
}).openapi("Product");
var productCreateSchema = z.object({
  title: z.string().trim().min(3).max(200),
  slug: slugSchema,
  sku: z.string().trim().min(1).max(64),
  description: z.string().trim().max(2e4).nullable().optional(),
  categoryId: uuidSchema,
  brandId: uuidSchema.nullable().optional(),
  oemNumber: z.string().trim().max(80).nullable().optional(),
  condition: productConditionSchema,
  grade: productGradeSchema.nullable().optional(),
  price: moneySchema,
  mrp: moneySchema.nullable().optional(),
  specialOfferPrice: moneySchema.nullable().optional(),
  vehicleMake: z.string().trim().max(80).nullable().optional(),
  vehicleModel: z.string().trim().max(80).nullable().optional(),
  vehicleVariant: z.string().trim().max(80).nullable().optional(),
  vehicleYear: modelYearSchema.nullable().optional(),
  quantity: z.number().int().min(0).default(0),
  status: productStatusSchema.default("draft"),
  warrantyDays: z.number().int().min(0).nullable().optional(),
  weightGrams: z.number().int().min(0),
  dimensions: dimensionsSchema.nullable().optional(),
  hsnCode: z.string().trim().regex(/^[0-9]{4,8}$/).nullable().optional(),
  taxRate: percentSchema.default(0),
  isFeatured: z.boolean().default(false),
  media: z.array(productMediaCreateSchema).max(5).default([]),
  crossRefs: z.array(productCrossRefCreateSchema).max(50).default([])
}).refine((v) => v.condition === "new" || v.grade != null, {
  path: ["grade"],
  message: "grade is required for used products"
}).refine((v) => v.condition === "new" || v.media.length >= 3, {
  path: ["media"],
  message: "used products need at least three photos"
}).openapi("ProductCreate");
var productUpdateSchema = z.object({
  title: z.string().trim().min(3).max(200),
  slug: slugSchema,
  sku: z.string().trim().min(1).max(64),
  description: z.string().trim().max(2e4).nullable(),
  categoryId: uuidSchema,
  brandId: uuidSchema.nullable(),
  oemNumber: z.string().trim().max(80).nullable(),
  condition: productConditionSchema,
  grade: productGradeSchema.nullable(),
  price: moneySchema,
  mrp: moneySchema.nullable(),
  specialOfferPrice: moneySchema.nullable(),
  vehicleMake: z.string().trim().max(80).nullable(),
  vehicleModel: z.string().trim().max(80).nullable(),
  vehicleVariant: z.string().trim().max(80).nullable(),
  vehicleYear: modelYearSchema.nullable(),
  quantity: z.number().int().min(0),
  status: productStatusSchema,
  warrantyDays: z.number().int().min(0).nullable(),
  weightGrams: z.number().int().min(0),
  dimensions: dimensionsSchema.nullable(),
  hsnCode: z.string().trim().regex(/^[0-9]{4,8}$/).nullable(),
  taxRate: percentSchema,
  isFeatured: z.boolean()
}).partial().openapi("ProductUpdate");
var productSortSchema = z.enum(["createdAt", "updatedAt", "price", "title", "quantity"]).openapi("ProductSort");
var productQuerySchema = listQuerySchema.extend(vehicleSelectionSchema.shape).extend({
  sort: productSortSchema.optional(),
  categoryId: uuidSchema.optional(),
  categorySlug: slugSchema.optional(),
  brandId: uuidSchema.optional(),
  condition: productConditionSchema.optional(),
  grade: productGradeSchema.optional(),
  status: productStatusSchema.optional(),
  minPrice: moneySchema.optional(),
  maxPrice: moneySchema.optional(),
  inStock: booleanQuerySchema.optional(),
  isFeatured: booleanQuerySchema.optional(),
  oemNumber: z.string().trim().max(80).optional(),
  userVehicleId: uuidSchema.optional(),
  fitmentYear: z.coerce.number().int().min(1900).max(2200).optional()
}).openapi("ProductQuery");
var facetBucketSchema = z.object({ value: z.string(), label: z.string(), count: z.number().int().min(0) }).openapi("FacetBucket");
var catalogFacetsSchema = z.object({
  categories: z.array(facetBucketSchema),
  brands: z.array(facetBucketSchema),
  conditions: z.array(facetBucketSchema),
  priceRange: z.object({ min: moneySchema, max: moneySchema }).nullable()
}).openapi("CatalogFacets");
var catalogSearchQuerySchema = productQuerySchema.extend({ withFacets: booleanQuerySchema.optional() }).openapi("CatalogSearchQuery");
var catalogSearchResultSchema = z.object({
  items: z.array(productListItemSchema),
  facets: catalogFacetsSchema.nullable(),
  appliedVehicle: vehicleSelectionSchema.nullable()
}).openapi("CatalogSearchResult");
var productSuggestionSchema = z.object({ id: uuidSchema, title: z.string(), slug: slugSchema, kind: z.string() }).openapi("ProductSuggestion");
var productCompatibilityQuerySchema = z.object({ year: modelYearSchema.optional() }).extend(vehicleSelectionSchema.shape).openapi("ProductCompatibilityQuery");
var productVisualSearchQuerySchema = z.object({ imageUrl: urlSchema, limit: z.coerce.number().int().min(1).max(24).default(12) }).openapi("ProductVisualSearchQuery");
var ProductReviewStatus = {
  PENDING: "pending",
  APPROVED: "approved",
  REJECTED: "rejected"
};
var productReviewStatusSchema = z.enum(ProductReviewStatus).openapi("ProductReviewStatus");
var productReviewSchema = z.object({
  id: uuidSchema,
  productId: uuidSchema,
  productTitle: z.string(),
  userId: uuidSchema,
  userName: z.string(),
  rating: z.number().int().min(1).max(5),
  body: z.string().nullable(),
  status: productReviewStatusSchema,
  createdAt: timestampSchema,
  updatedAt: timestampSchema
}).openapi("ProductReview");
var productReviewQuerySchema = listQuerySchema.extend({
  productId: uuidSchema.optional(),
  status: productReviewStatusSchema.optional(),
  minRating: z.coerce.number().int().min(1).max(5).optional()
}).openapi("ProductReviewQuery");
var productReviewUpdateSchema = z.object({ status: productReviewStatusSchema }).openapi("ProductReviewUpdate");
var productReviewCreateSchema = z.object({
  productId: uuidSchema,
  rating: z.number().int().min(1).max(5),
  body: z.string().trim().max(2e3).nullable().optional()
}).openapi("ProductReviewCreate");
var productReviewEligibilityQuerySchema = z.object({ productId: uuidSchema }).openapi("ProductReviewEligibilityQuery");
var productReviewEligibilityReasonSchema = z.enum(["not_purchased", "already_reviewed"]).openapi("ProductReviewEligibilityReason");
var productReviewEligibilitySchema = z.object({
  canReview: z.boolean(),
  reason: productReviewEligibilityReasonSchema.optional()
}).openapi("ProductReviewEligibility");

// src/inventory/index.ts
var inventory_exports = {};
__export(inventory_exports, {
  StockAdjustmentReason: () => StockAdjustmentReason,
  StockState: () => StockState,
  inventorySummarySchema: () => inventorySummarySchema,
  stockAdjustmentCreateSchema: () => stockAdjustmentCreateSchema,
  stockAdjustmentQuerySchema: () => stockAdjustmentQuerySchema,
  stockAdjustmentReasonSchema: () => stockAdjustmentReasonSchema,
  stockAdjustmentSchema: () => stockAdjustmentSchema,
  stockAdjustmentUpdateSchema: () => stockAdjustmentUpdateSchema,
  stockBulkAdjustSchema: () => stockBulkAdjustSchema,
  stockLevelQuerySchema: () => stockLevelQuerySchema,
  stockLevelSchema: () => stockLevelSchema,
  stockLevelUpdateSchema: () => stockLevelUpdateSchema,
  stockStateSchema: () => stockStateSchema
});
var StockAdjustmentReason = {
  PURCHASE: "purchase",
  SALE: "sale",
  RETURN: "return",
  DAMAGE: "damage",
  CORRECTION: "correction",
  TRANSFER: "transfer"
};
var stockAdjustmentReasonSchema = z.enum(StockAdjustmentReason).openapi("StockAdjustmentReason");
var StockState = {
  IN_STOCK: "in_stock",
  LOW_STOCK: "low_stock",
  OUT_OF_STOCK: "out_of_stock"
};
var stockStateSchema = z.enum(StockState).openapi("StockState");
var stockLevelSchema = z.object({
  productId: uuidSchema,
  product: productSummarySchema,
  onHand: z.number().int().min(0),
  reserved: z.number().int().min(0),
  available: z.number().int().min(0),
  reorderLevel: z.number().int().min(0),
  state: stockStateSchema,
  lastAdjustedAt: timestampSchema.nullable(),
  updatedAt: timestampSchema
}).openapi("StockLevel");
var stockLevelUpdateSchema = z.object({ reorderLevel: z.number().int().min(0) }).partial().openapi("StockLevelUpdate");
var stockLevelQuerySchema = listQuerySchema.extend({
  productId: uuidSchema.optional(),
  categoryId: uuidSchema.optional(),
  brandId: uuidSchema.optional(),
  state: stockStateSchema.optional(),
  lowStockOnly: booleanQuerySchema.optional()
}).openapi("StockLevelQuery");
var stockAdjustmentSchema = z.object({
  id: uuidSchema,
  productId: uuidSchema,
  product: productSummarySchema,
  delta: z.number().int(),
  onHandAfter: z.number().int().min(0),
  reason: stockAdjustmentReasonSchema,
  note: z.string().nullable(),
  referenceType: z.string().nullable(),
  referenceId: uuidSchema.nullable(),
  actor: actorSchema.nullable(),
  createdAt: timestampSchema
}).openapi("StockAdjustment");
var stockAdjustmentCreateSchema = z.object({
  productId: uuidSchema,
  delta: z.number().int(),
  reason: stockAdjustmentReasonSchema.default(StockAdjustmentReason.CORRECTION),
  note: z.string().trim().max(500).nullable().optional()
}).refine((v) => v.delta !== 0, { path: ["delta"], message: "delta must not be zero" }).openapi("StockAdjustmentCreate");
var stockAdjustmentUpdateSchema = z.object({ note: z.string().trim().max(500).nullable() }).partial().openapi("StockAdjustmentUpdate");
var stockAdjustmentQuerySchema = listQuerySchema.extend({
  productId: uuidSchema.optional(),
  reason: stockAdjustmentReasonSchema.optional(),
  dateFrom: timestampSchema.optional(),
  dateTo: timestampSchema.optional()
}).openapi("StockAdjustmentQuery");
var stockBulkAdjustSchema = z.object({ adjustments: z.array(stockAdjustmentCreateSchema).min(1).max(200) }).openapi("StockBulkAdjust");
var inventorySummarySchema = z.object({
  trackedProducts: z.number().int().min(0),
  inStock: z.number().int().min(0),
  lowStock: z.number().int().min(0),
  outOfStock: z.number().int().min(0),
  totalUnits: z.number().int().min(0)
}).openapi("InventorySummary");

// src/cart/index.ts
var cart_exports = {};
__export(cart_exports, {
  CartItemIssue: () => CartItemIssue,
  cartCountSchema: () => cartCountSchema,
  cartFitmentWarningSchema: () => cartFitmentWarningSchema,
  cartItemCreateSchema: () => cartItemCreateSchema,
  cartItemIssueSchema: () => cartItemIssueSchema,
  cartItemParamSchema: () => cartItemParamSchema,
  cartItemSchema: () => cartItemSchema,
  cartItemUpdateSchema: () => cartItemUpdateSchema,
  cartMergeRequestSchema: () => cartMergeRequestSchema,
  cartQuerySchema: () => cartQuerySchema,
  cartSchema: () => cartSchema,
  cartSummarySchema: () => cartSummarySchema
});
var CartItemIssue = {
  OUT_OF_STOCK: "out_of_stock",
  QUANTITY_REDUCED: "quantity_reduced",
  PRICE_CHANGED: "price_changed",
  UNAVAILABLE: "unavailable",
  FITMENT_MISMATCH: "fitment_mismatch"
};
var cartItemIssueSchema = z.enum(CartItemIssue).openapi("CartItemIssue");
var cartItemSchema = z.object({
  id: uuidSchema,
  productId: uuidSchema,
  product: productSummarySchema,
  quantity: z.number().int().positive(),
  unitPrice: moneySchema,
  lineTotal: moneySchema,
  maxQuantity: z.number().int().min(0),
  issues: z.array(cartItemIssueSchema),
  addedAt: timestampSchema
}).openapi("CartItem");
var cartFitmentWarningSchema = z.object({
  productId: uuidSchema,
  userVehicleId: uuidSchema.nullable(),
  message: z.string()
}).openapi("CartFitmentWarning");
var cartSchema = z.object({
  id: uuidSchema,
  userId: uuidSchema,
  items: z.array(cartItemSchema),
  itemCount: z.number().int().min(0),
  subtotal: moneySchema,
  currency: currencyCodeSchema,
  fitmentWarnings: z.array(cartFitmentWarningSchema),
  hasIssues: z.boolean(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema
}).openapi("Cart");
var cartItemCreateSchema = z.object({
  productId: uuidSchema,
  quantity: z.number().int().positive().max(99).default(1),
  userVehicleId: uuidSchema.nullable().optional()
}).openapi("CartItemCreate");
var cartItemUpdateSchema = z.object({ quantity: z.number().int().positive().max(99) }).partial().openapi("CartItemUpdate");
var cartItemParamSchema = z.object({ itemId: uuidSchema }).openapi("CartItemParam");
var cartMergeRequestSchema = z.object({ items: z.array(cartItemCreateSchema).min(1).max(100) }).openapi("CartMergeRequest");
var cartQuerySchema = listQuerySchema.extend({
  userId: uuidSchema.optional(),
  updatedBefore: timestampSchema.optional(),
  minSubtotal: moneySchema.optional()
}).openapi("CartQuery");
var cartSummarySchema = z.object({
  id: uuidSchema,
  userId: uuidSchema,
  userName: z.string(),
  userEmail: z.string(),
  itemCount: z.number().int().min(0),
  subtotal: moneySchema,
  currency: currencyCodeSchema,
  updatedAt: timestampSchema
}).openapi("CartSummary");
var cartCountSchema = z.object({ itemCount: z.number().int().min(0), subtotal: moneySchema }).openapi("CartCount");

// src/checkout/index.ts
var checkout_exports = {};
__export(checkout_exports, {
  AddressType: () => AddressType,
  CheckoutIssue: () => CheckoutIssue,
  addressCreateSchema: () => addressCreateSchema,
  addressQuerySchema: () => addressQuerySchema,
  addressSchema: () => addressSchema,
  addressSelectionSchema: () => addressSelectionSchema,
  addressTypeSchema: () => addressTypeSchema,
  addressUpdateSchema: () => addressUpdateSchema,
  checkoutIssueDetailSchema: () => checkoutIssueDetailSchema,
  checkoutIssueSchema: () => checkoutIssueSchema,
  checkoutQuoteRequestSchema: () => checkoutQuoteRequestSchema,
  checkoutQuoteSchema: () => checkoutQuoteSchema,
  checkoutRequestSchema: () => checkoutRequestSchema,
  checkoutResultSchema: () => checkoutResultSchema,
  checkoutTotalsSchema: () => checkoutTotalsSchema,
  taxLineSchema: () => taxLineSchema
});

// src/promotions/index.ts
var promotions_exports = {};
__export(promotions_exports, {
  CouponRejectionReason: () => CouponRejectionReason,
  CouponStatus: () => CouponStatus,
  CouponType: () => CouponType,
  HomepageBannerStatus: () => HomepageBannerStatus,
  couponAppliedSchema: () => couponAppliedSchema,
  couponCodeSchema: () => couponCodeSchema,
  couponCreateSchema: () => couponCreateSchema,
  couponQuerySchema: () => couponQuerySchema,
  couponRedemptionQuerySchema: () => couponRedemptionQuerySchema,
  couponRedemptionSchema: () => couponRedemptionSchema,
  couponRejectionReasonSchema: () => couponRejectionReasonSchema,
  couponSchema: () => couponSchema,
  couponScopeSchema: () => couponScopeSchema,
  couponStatusSchema: () => couponStatusSchema,
  couponTypeSchema: () => couponTypeSchema,
  couponUpdateSchema: () => couponUpdateSchema,
  couponValidateRequestSchema: () => couponValidateRequestSchema,
  couponValidationSchema: () => couponValidationSchema,
  homepageBannerCreateSchema: () => homepageBannerCreateSchema,
  homepageBannerQuerySchema: () => homepageBannerQuerySchema,
  homepageBannerSchema: () => homepageBannerSchema,
  homepageBannerStatusSchema: () => homepageBannerStatusSchema,
  homepageBannerUpdateSchema: () => homepageBannerUpdateSchema
});
var CouponType = {
  PERCENTAGE: "percentage",
  FIXED: "fixed",
  FREE_SHIPPING: "free_shipping"
};
var couponTypeSchema = z.enum(CouponType).openapi("CouponType");
var CouponStatus = {
  SCHEDULED: "scheduled",
  ACTIVE: "active",
  EXPIRED: "expired",
  EXHAUSTED: "exhausted",
  DISABLED: "disabled"
};
var couponStatusSchema = z.enum(CouponStatus).openapi("CouponStatus");
var CouponRejectionReason = {
  NOT_FOUND: "not_found",
  EXPIRED: "expired",
  NOT_STARTED: "not_started",
  DISABLED: "disabled",
  USAGE_LIMIT_REACHED: "usage_limit_reached",
  USER_LIMIT_REACHED: "user_limit_reached",
  MIN_ORDER_NOT_MET: "min_order_not_met",
  NOT_APPLICABLE: "not_applicable"
};
var couponRejectionReasonSchema = z.enum(CouponRejectionReason).openapi("CouponRejectionReason");
var couponCodeSchema = z.string().trim().toUpperCase().min(3).max(32).regex(/^[A-Z0-9_-]+$/).openapi("CouponCode");
var couponScopeSchema = z.object({
  categoryIds: z.array(uuidSchema).max(100),
  brandIds: z.array(uuidSchema).max(100),
  productIds: z.array(uuidSchema).max(500)
}).openapi("CouponScope");
var couponSchema = z.object({
  id: uuidSchema,
  code: couponCodeSchema,
  description: z.string().nullable(),
  type: couponTypeSchema,
  value: moneySchema,
  percentValue: percentSchema.nullable(),
  minOrderValue: moneySchema.nullable(),
  maxDiscount: moneySchema.nullable(),
  usageLimit: z.number().int().positive().nullable(),
  perUserLimit: z.number().int().positive().nullable(),
  usedCount: z.number().int().min(0),
  startsAt: timestampSchema,
  endsAt: timestampSchema.nullable(),
  isActive: z.boolean(),
  status: couponStatusSchema,
  scope: couponScopeSchema,
  createdAt: timestampSchema,
  updatedAt: timestampSchema
}).openapi("Coupon");
var couponCreateSchema = z.object({
  code: couponCodeSchema,
  description: z.string().trim().max(500).nullable().optional(),
  type: couponTypeSchema,
  value: moneySchema.optional(),
  percentValue: percentSchema.optional(),
  minOrderValue: moneySchema.nullable().optional(),
  maxDiscount: moneySchema.nullable().optional(),
  usageLimit: z.number().int().positive().nullable().optional(),
  perUserLimit: z.number().int().positive().nullable().optional(),
  startsAt: timestampSchema,
  endsAt: timestampSchema.nullable().optional(),
  isActive: z.boolean().default(true),
  scope: couponScopeSchema.default({ categoryIds: [], brandIds: [], productIds: [] })
}).refine((v) => v.type !== "percentage" || v.percentValue != null, {
  path: ["percentValue"],
  message: "percentValue is required for a percentage coupon"
}).refine((v) => v.type !== "fixed" || v.value != null, {
  path: ["value"],
  message: "value is required for a fixed coupon"
}).openapi("CouponCreate");
var couponUpdateSchema = z.object({
  description: z.string().trim().max(500).nullable(),
  value: moneySchema,
  percentValue: percentSchema,
  minOrderValue: moneySchema.nullable(),
  maxDiscount: moneySchema.nullable(),
  usageLimit: z.number().int().positive().nullable(),
  perUserLimit: z.number().int().positive().nullable(),
  startsAt: timestampSchema,
  endsAt: timestampSchema.nullable(),
  isActive: z.boolean(),
  scope: couponScopeSchema
}).partial().openapi("CouponUpdate");
var couponQuerySchema = listQuerySchema.extend({
  type: couponTypeSchema.optional(),
  status: couponStatusSchema.optional(),
  isActive: booleanQuerySchema.optional(),
  activeOn: timestampSchema.optional()
}).openapi("CouponQuery");
var couponRedemptionSchema = z.object({
  id: uuidSchema,
  couponId: uuidSchema,
  code: couponCodeSchema,
  userId: uuidSchema,
  userName: z.string(),
  orderId: uuidSchema,
  orderNumber: z.string(),
  discountAmount: moneySchema,
  redeemedAt: timestampSchema
}).openapi("CouponRedemption");
var couponRedemptionQuerySchema = listQuerySchema.extend({
  couponId: uuidSchema.optional(),
  userId: uuidSchema.optional(),
  dateFrom: timestampSchema.optional(),
  dateTo: timestampSchema.optional()
}).openapi("CouponRedemptionQuery");
var couponValidateRequestSchema = z.object({ code: couponCodeSchema, cartId: uuidSchema.optional() }).openapi("CouponValidateRequest");
var couponValidationSchema = z.object({
  valid: z.boolean(),
  code: couponCodeSchema,
  type: couponTypeSchema.nullable(),
  discount: moneySchema,
  freeShipping: z.boolean(),
  reason: couponRejectionReasonSchema.nullable(),
  message: z.string().nullable()
}).openapi("CouponValidation");
var couponAppliedSchema = z.object({
  id: uuidSchema,
  code: couponCodeSchema,
  type: couponTypeSchema,
  discount: moneySchema,
  freeShipping: z.boolean()
}).openapi("CouponApplied");
var HomepageBannerStatus = { ACTIVE: "active", INACTIVE: "inactive" };
var homepageBannerStatusSchema = z.enum(HomepageBannerStatus).openapi("HomepageBannerStatus");
var homepageBannerSchema = z.object({
  id: uuidSchema,
  title: z.string(),
  imageUrl: urlSchema,
  linkUrl: urlSchema.nullable(),
  sortOrder: z.number().int(),
  isActive: z.boolean(),
  startsAt: timestampSchema.nullable(),
  endsAt: timestampSchema.nullable(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema
}).openapi("HomepageBanner");
var homepageBannerCreateSchema = z.object({
  title: z.string().trim().min(1).max(120),
  imageUrl: urlSchema,
  linkUrl: urlSchema.nullable().optional(),
  sortOrder: z.number().int().min(0).default(0),
  isActive: z.boolean().default(true),
  startsAt: timestampSchema.nullable().optional(),
  endsAt: timestampSchema.nullable().optional()
}).openapi("HomepageBannerCreate");
var homepageBannerUpdateSchema = homepageBannerCreateSchema.partial().openapi("HomepageBannerUpdate");
var homepageBannerQuerySchema = listQuerySchema.extend({ isActive: booleanQuerySchema.optional() }).openapi("HomepageBannerQuery");

// src/payments/index.ts
var payments_exports = {};
__export(payments_exports, {
  PaymentMethod: () => PaymentMethod,
  PaymentProvider: () => PaymentProvider,
  PaymentStatus: () => PaymentStatus,
  RefundReason: () => RefundReason,
  RefundStatus: () => RefundStatus,
  paymentCreateSchema: () => paymentCreateSchema,
  paymentIntentRequestSchema: () => paymentIntentRequestSchema,
  paymentIntentSchema: () => paymentIntentSchema,
  paymentMethodSchema: () => paymentMethodSchema,
  paymentProviderSchema: () => paymentProviderSchema,
  paymentQuerySchema: () => paymentQuerySchema,
  paymentSchema: () => paymentSchema,
  paymentStatusSchema: () => paymentStatusSchema,
  paymentUpdateSchema: () => paymentUpdateSchema,
  paymentVerifyRequestSchema: () => paymentVerifyRequestSchema,
  paymentVerifyResponseSchema: () => paymentVerifyResponseSchema,
  paymentWebhookEventSchema: () => paymentWebhookEventSchema,
  refundCreateSchema: () => refundCreateSchema,
  refundQuerySchema: () => refundQuerySchema,
  refundReasonSchema: () => refundReasonSchema,
  refundSchema: () => refundSchema,
  refundStatusSchema: () => refundStatusSchema,
  refundUpdateSchema: () => refundUpdateSchema
});
var PaymentProvider = { RAZORPAY: "razorpay", COD: "cod" };
var paymentProviderSchema = z.enum(PaymentProvider).openapi("PaymentProvider");
var PaymentMethod = {
  CARD: "card",
  UPI: "upi",
  NETBANKING: "netbanking",
  WALLET: "wallet",
  COD: "cod"
};
var paymentMethodSchema = z.enum(PaymentMethod).openapi("PaymentMethod");
var PaymentStatus = {
  CREATED: "created",
  PENDING: "pending",
  AUTHORIZED: "authorized",
  CAPTURED: "captured",
  FAILED: "failed",
  REFUNDED: "refunded",
  PARTIALLY_REFUNDED: "partially_refunded"
};
var paymentStatusSchema = z.enum(PaymentStatus).openapi("PaymentStatus");
var RefundStatus = {
  REQUESTED: "requested",
  PROCESSING: "processing",
  COMPLETED: "completed",
  FAILED: "failed"
};
var refundStatusSchema = z.enum(RefundStatus).openapi("RefundStatus");
var RefundReason = {
  CANCELLED: "cancelled",
  RETURNED: "returned",
  DAMAGED: "damaged",
  WRONG_ITEM: "wrong_item",
  GOODWILL: "goodwill"
};
var refundReasonSchema = z.enum(RefundReason).openapi("RefundReason");
var paymentIntentRequestSchema = z.object({
  orderId: uuidSchema,
  provider: paymentProviderSchema,
  idempotencyKey: idempotencyKeySchema
}).openapi("PaymentIntentRequest");
var paymentIntentSchema = z.object({
  id: uuidSchema,
  orderId: uuidSchema,
  provider: paymentProviderSchema,
  providerOrderId: z.string().nullable(),
  providerKeyId: z.string().nullable(),
  amount: moneySchema,
  currency: currencyCodeSchema,
  status: paymentStatusSchema,
  expiresAt: timestampSchema.nullable(),
  createdAt: timestampSchema
}).openapi("PaymentIntent");
var paymentVerifyRequestSchema = z.object({
  orderId: uuidSchema,
  provider: paymentProviderSchema,
  providerOrderId: z.string().trim().min(1).max(128),
  providerPaymentId: z.string().trim().min(1).max(128),
  providerSignature: z.string().trim().min(1).max(512),
  idempotencyKey: idempotencyKeySchema
}).openapi("PaymentVerifyRequest");
var paymentSchema = z.object({
  id: uuidSchema,
  orderId: uuidSchema,
  orderNumber: z.string(),
  userId: uuidSchema,
  provider: paymentProviderSchema,
  method: paymentMethodSchema.nullable(),
  amount: moneySchema,
  refundedAmount: moneySchema,
  currency: currencyCodeSchema,
  status: paymentStatusSchema,
  providerOrderId: z.string().nullable(),
  providerPaymentId: z.string().nullable(),
  failureReason: z.string().nullable(),
  capturedAt: timestampSchema.nullable(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema
}).openapi("Payment");
var paymentVerifyResponseSchema = z.object({ payment: paymentSchema, orderStatus: z.string() }).openapi("PaymentVerifyResponse");
var paymentCreateSchema = z.object({
  orderId: uuidSchema,
  provider: paymentProviderSchema,
  method: paymentMethodSchema.optional(),
  amount: moneySchema,
  idempotencyKey: idempotencyKeySchema
}).openapi("PaymentCreate");
var paymentUpdateSchema = z.object({ status: paymentStatusSchema, failureReason: z.string().trim().max(500).nullable() }).partial().openapi("PaymentUpdate");
var paymentQuerySchema = listQuerySchema.extend({
  orderId: uuidSchema.optional(),
  userId: uuidSchema.optional(),
  provider: paymentProviderSchema.optional(),
  method: paymentMethodSchema.optional(),
  status: paymentStatusSchema.optional(),
  dateFrom: timestampSchema.optional(),
  dateTo: timestampSchema.optional()
}).openapi("PaymentQuery");
var refundSchema = z.object({
  id: uuidSchema,
  paymentId: uuidSchema,
  orderId: uuidSchema,
  orderNumber: z.string(),
  amount: moneySchema,
  currency: currencyCodeSchema,
  reason: refundReasonSchema,
  note: z.string().nullable(),
  status: refundStatusSchema,
  providerRefundId: z.string().nullable(),
  failureReason: z.string().nullable(),
  processedAt: timestampSchema.nullable(),
  createdAt: timestampSchema
}).openapi("Refund");
var refundCreateSchema = z.object({
  paymentId: uuidSchema,
  amount: moneySchema,
  reason: refundReasonSchema,
  note: z.string().trim().max(500).nullable().optional(),
  idempotencyKey: idempotencyKeySchema
}).openapi("RefundCreate");
var refundUpdateSchema = z.object({
  status: refundStatusSchema,
  note: z.string().trim().max(500).nullable(),
  failureReason: z.string().trim().max(500).nullable()
}).partial().openapi("RefundUpdate");
var refundQuerySchema = listQuerySchema.extend({
  orderId: uuidSchema.optional(),
  paymentId: uuidSchema.optional(),
  status: refundStatusSchema.optional(),
  reason: refundReasonSchema.optional(),
  dateFrom: timestampSchema.optional(),
  dateTo: timestampSchema.optional()
}).openapi("RefundQuery");
var paymentWebhookEventSchema = z.object({
  provider: paymentProviderSchema,
  event: z.string().trim().min(1).max(120),
  providerEventId: z.string().trim().min(1).max(128),
  payload: z.record(z.string(), z.unknown())
}).openapi("PaymentWebhookEvent");

// src/shipping/index.ts
var shipping_exports = {};
__export(shipping_exports, {
  ShipmentStatus: () => ShipmentStatus,
  carrierSchema: () => carrierSchema,
  pincodeCheckRequestSchema: () => pincodeCheckRequestSchema,
  pincodeCheckSchema: () => pincodeCheckSchema,
  shipmentCreateSchema: () => shipmentCreateSchema,
  shipmentQuerySchema: () => shipmentQuerySchema,
  shipmentSchema: () => shipmentSchema,
  shipmentStatusSchema: () => shipmentStatusSchema,
  shipmentTrackingSchema: () => shipmentTrackingSchema,
  shipmentUpdateSchema: () => shipmentUpdateSchema,
  shippingQuoteRequestSchema: () => shippingQuoteRequestSchema,
  shippingQuoteSchema: () => shippingQuoteSchema,
  trackingEventCreateSchema: () => trackingEventCreateSchema,
  trackingEventSchema: () => trackingEventSchema,
  trackingNumberSchema: () => trackingNumberSchema,
  weightBandCreateSchema: () => weightBandCreateSchema,
  weightBandQuerySchema: () => weightBandQuerySchema,
  weightBandSchema: () => weightBandSchema,
  weightBandUpdateSchema: () => weightBandUpdateSchema
});
var ShipmentStatus = {
  PENDING: "pending",
  LABEL_CREATED: "label_created",
  PICKED_UP: "picked_up",
  IN_TRANSIT: "in_transit",
  OUT_FOR_DELIVERY: "out_for_delivery",
  DELIVERED: "delivered",
  FAILED: "failed",
  RETURNED: "returned"
};
var shipmentStatusSchema = z.enum(ShipmentStatus).openapi("ShipmentStatus");
var carrierSchema = z.string().trim().min(1).max(80).openapi("Carrier");
var trackingNumberSchema = z.string().trim().min(3).max(64).regex(/^[A-Za-z0-9-]+$/).openapi("TrackingNumber");
var shipmentSchema = z.object({
  id: uuidSchema,
  orderId: uuidSchema,
  orderNumber: z.string(),
  carrier: carrierSchema,
  trackingNumber: trackingNumberSchema.nullable(),
  trackingUrl: urlSchema.nullable(),
  status: shipmentStatusSchema,
  weightGrams: z.number().int().min(0),
  shippingCharge: moneySchema,
  dispatchedAt: timestampSchema.nullable(),
  deliveredAt: timestampSchema.nullable(),
  estimatedDeliveryAt: timestampSchema.nullable(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema
}).openapi("Shipment");
var shipmentCreateSchema = z.object({
  orderId: uuidSchema,
  carrier: carrierSchema,
  trackingNumber: trackingNumberSchema.nullable().optional(),
  trackingUrl: urlSchema.nullable().optional(),
  weightGrams: z.number().int().min(0),
  shippingCharge: moneySchema,
  estimatedDeliveryAt: timestampSchema.nullable().optional()
}).openapi("ShipmentCreate");
var shipmentUpdateSchema = z.object({
  carrier: carrierSchema,
  trackingNumber: trackingNumberSchema.nullable(),
  trackingUrl: urlSchema.nullable(),
  status: shipmentStatusSchema,
  weightGrams: z.number().int().min(0),
  shippingCharge: moneySchema,
  dispatchedAt: timestampSchema.nullable(),
  deliveredAt: timestampSchema.nullable(),
  estimatedDeliveryAt: timestampSchema.nullable()
}).partial().openapi("ShipmentUpdate");
var shipmentQuerySchema = listQuerySchema.extend({
  orderId: uuidSchema.optional(),
  status: shipmentStatusSchema.optional(),
  carrier: carrierSchema.optional(),
  trackingNumber: trackingNumberSchema.optional(),
  dateFrom: timestampSchema.optional(),
  dateTo: timestampSchema.optional()
}).openapi("ShipmentQuery");
var trackingEventSchema = z.object({
  id: uuidSchema,
  shipmentId: uuidSchema,
  status: shipmentStatusSchema,
  description: z.string(),
  location: z.string().nullable(),
  occurredAt: timestampSchema
}).openapi("TrackingEvent");
var trackingEventCreateSchema = z.object({
  status: shipmentStatusSchema,
  description: z.string().trim().min(1).max(240),
  location: z.string().trim().max(120).nullable().optional(),
  occurredAt: timestampSchema
}).openapi("TrackingEventCreate");
var shipmentTrackingSchema = z.object({ shipment: shipmentSchema, events: z.array(trackingEventSchema) }).openapi("ShipmentTracking");
var weightBandSchema = z.object({
  id: uuidSchema,
  label: z.string(),
  minGrams: z.number().int().min(0),
  maxGrams: z.number().int().positive().nullable(),
  charge: moneySchema,
  etaDays: z.number().int().positive().nullable(),
  sortOrder: z.number().int(),
  isActive: z.boolean(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema
}).openapi("WeightBand");
var weightBandCreateSchema = z.object({
  label: z.string().trim().min(1).max(80),
  minGrams: z.number().int().min(0),
  maxGrams: z.number().int().positive().nullable().optional(),
  charge: moneySchema,
  etaDays: z.number().int().positive().nullable().optional(),
  sortOrder: z.number().int().min(0).default(0),
  isActive: z.boolean().default(true)
}).refine((v) => v.maxGrams == null || v.maxGrams > v.minGrams, {
  path: ["maxGrams"],
  message: "maxGrams must be greater than minGrams"
}).openapi("WeightBandCreate");
var weightBandUpdateSchema = z.object({
  label: z.string().trim().min(1).max(80),
  minGrams: z.number().int().min(0),
  maxGrams: z.number().int().positive().nullable(),
  charge: moneySchema,
  etaDays: z.number().int().positive().nullable(),
  sortOrder: z.number().int().min(0),
  isActive: z.boolean()
}).partial().openapi("WeightBandUpdate");
var weightBandQuerySchema = listQuerySchema.extend({ isActive: booleanQuerySchema.optional() }).openapi("WeightBandQuery");
var shippingQuoteRequestSchema = z.object({
  pincode: pincodeSchema,
  weightGrams: z.number().int().min(0),
  orderValue: moneySchema
}).openapi("ShippingQuoteRequest");
var shippingQuoteSchema = z.object({
  serviceable: z.boolean(),
  charge: moneySchema,
  freeShippingApplied: z.boolean(),
  band: weightBandSchema.nullable(),
  etaDays: z.number().int().positive().nullable(),
  message: z.string().nullable()
}).openapi("ShippingQuote");
var pincodeCheckRequestSchema = z.object({ pincode: pincodeSchema }).openapi("PincodeCheckRequest");
var pincodeCheckSchema = z.object({ pincode: pincodeSchema, serviceable: z.boolean(), etaDays: z.number().int().nullable() }).openapi("PincodeCheck");

// src/checkout/index.ts
var AddressType = { SHIPPING: "shipping", BILLING: "billing", BOTH: "both" };
var addressTypeSchema = z.enum(AddressType).openapi("AddressType");
var addressSchema = z.object({
  id: uuidSchema,
  userId: uuidSchema,
  label: z.string().nullable(),
  fullName: z.string(),
  phone: phoneSchema,
  line1: z.string(),
  line2: z.string().nullable(),
  landmark: z.string().nullable(),
  city: z.string(),
  state: z.string(),
  pincode: pincodeSchema,
  country: countryCodeSchema,
  type: addressTypeSchema,
  isDefaultShipping: z.boolean(),
  isDefaultBilling: z.boolean(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema
}).openapi("Address");
var addressCreateSchema = z.object({
  label: z.string().trim().max(40).nullable().optional(),
  fullName: z.string().trim().min(1).max(120),
  phone: phoneSchema,
  line1: z.string().trim().min(1).max(200),
  line2: z.string().trim().max(200).nullable().optional(),
  landmark: z.string().trim().max(120).nullable().optional(),
  city: z.string().trim().min(1).max(80),
  state: z.string().trim().min(1).max(80),
  pincode: pincodeSchema,
  country: countryCodeSchema,
  type: addressTypeSchema.default("both"),
  isDefaultShipping: z.boolean().default(false),
  isDefaultBilling: z.boolean().default(false)
}).openapi("AddressCreate");
var addressUpdateSchema = z.object({
  label: z.string().trim().max(40).nullable(),
  fullName: z.string().trim().min(1).max(120),
  phone: phoneSchema,
  line1: z.string().trim().min(1).max(200),
  line2: z.string().trim().max(200).nullable(),
  landmark: z.string().trim().max(120).nullable(),
  city: z.string().trim().min(1).max(80),
  state: z.string().trim().min(1).max(80),
  pincode: pincodeSchema,
  country: countryCodeSchema,
  type: addressTypeSchema,
  isDefaultShipping: z.boolean(),
  isDefaultBilling: z.boolean()
}).partial().openapi("AddressUpdate");
var addressQuerySchema = listQuerySchema.extend({ userId: uuidSchema.optional(), type: addressTypeSchema.optional() }).openapi("AddressQuery");
var taxLineSchema = z.object({
  label: z.string(),
  rate: percentSchema,
  taxableAmount: moneySchema,
  amount: moneySchema
}).openapi("TaxLine");
var CheckoutIssue = {
  CART_EMPTY: "cart_empty",
  ITEM_OUT_OF_STOCK: "item_out_of_stock",
  ITEM_PRICE_CHANGED: "item_price_changed",
  ADDRESS_MISSING: "address_missing",
  PINCODE_NOT_SERVICEABLE: "pincode_not_serviceable",
  COUPON_INVALID: "coupon_invalid",
  MINIMUM_ORDER_NOT_MET: "minimum_order_not_met"
};
var checkoutIssueSchema = z.enum(CheckoutIssue).openapi("CheckoutIssue");
var checkoutIssueDetailSchema = z.object({
  code: checkoutIssueSchema,
  message: z.string(),
  productId: uuidSchema.nullable(),
  blocking: z.boolean()
}).openapi("CheckoutIssueDetail");
var checkoutQuoteRequestSchema = z.object({
  shippingAddressId: uuidSchema.optional(),
  billingAddressId: uuidSchema.optional(),
  couponCode: couponCodeSchema.optional(),
  pincode: pincodeSchema.optional(),
  userVehicleId: uuidSchema.nullable().optional()
}).openapi("CheckoutQuoteRequest");
var checkoutTotalsSchema = z.object({
  subtotal: moneySchema,
  discount: moneySchema,
  taxTotal: moneySchema,
  shippingTotal: moneySchema,
  grandTotal: moneySchema,
  currency: currencyCodeSchema
}).openapi("CheckoutTotals");
var checkoutQuoteSchema = z.object({
  items: z.array(cartItemSchema),
  totals: checkoutTotalsSchema,
  taxBreakdown: z.array(taxLineSchema),
  shipping: shippingQuoteSchema,
  coupon: couponAppliedSchema.nullable(),
  shippingAddress: addressSchema.nullable(),
  billingAddress: addressSchema.nullable(),
  availableProviders: z.array(paymentProviderSchema),
  issues: z.array(checkoutIssueDetailSchema),
  canPlaceOrder: z.boolean(),
  expiresAt: timestampSchema
}).openapi("CheckoutQuote");
var checkoutRequestSchema = z.object({
  shippingAddressId: uuidSchema,
  billingAddressId: uuidSchema,
  couponCode: couponCodeSchema.nullable().optional(),
  provider: paymentProviderSchema,
  idempotencyKey: idempotencyKeySchema,
  expectedTotal: moneySchema,
  notes: z.string().trim().max(500).nullable().optional(),
  userVehicleId: uuidSchema.nullable().optional()
}).openapi("CheckoutRequest");
var checkoutResultSchema = z.object({
  orderId: uuidSchema,
  orderNumber: z.string(),
  status: z.string(),
  totals: checkoutTotalsSchema,
  payment: paymentIntentSchema.nullable()
}).openapi("CheckoutResult");
var addressSelectionSchema = z.object({ shippingAddressId: uuidSchema, billingAddressId: uuidSchema.optional() }).openapi("AddressSelection");

// src/orders/index.ts
var orders_exports = {};
__export(orders_exports, {
  CancellationReason: () => CancellationReason,
  OrderStatus: () => OrderStatus,
  cancellationReasonSchema: () => cancellationReasonSchema,
  orderAddressSchema: () => orderAddressSchema,
  orderAllowedTransitionsSchema: () => orderAllowedTransitionsSchema,
  orderCancelRequestSchema: () => orderCancelRequestSchema,
  orderCreateSchema: () => orderCreateSchema,
  orderCustomerSchema: () => orderCustomerSchema,
  orderItemCreateSchema: () => orderItemCreateSchema,
  orderItemSchema: () => orderItemSchema,
  orderListItemSchema: () => orderListItemSchema,
  orderNumberSchema: () => orderNumberSchema,
  orderQuerySchema: () => orderQuerySchema,
  orderSchema: () => orderSchema,
  orderSortSchema: () => orderSortSchema,
  orderStatusCountSchema: () => orderStatusCountSchema,
  orderStatusHistoryEntrySchema: () => orderStatusHistoryEntrySchema,
  orderStatusSchema: () => orderStatusSchema,
  orderStatusTransitionSchema: () => orderStatusTransitionSchema,
  orderTotalsSchema: () => orderTotalsSchema,
  orderTransitionRequestSchema: () => orderTransitionRequestSchema,
  orderUpdateSchema: () => orderUpdateSchema
});
var OrderStatus = {
  PENDING: "pending",
  CONFIRMED: "confirmed",
  PACKED: "packed",
  SHIPPED: "shipped",
  OUT_FOR_DELIVERY: "out_for_delivery",
  DELIVERED: "delivered",
  CANCELLED: "cancelled",
  RETURNED: "returned",
  REFUNDED: "refunded"
};
var orderStatusSchema = z.enum(OrderStatus).openapi("OrderStatus");
var CancellationReason = {
  CUSTOMER_REQUEST: "customer_request",
  OUT_OF_STOCK: "out_of_stock",
  PAYMENT_FAILED: "payment_failed",
  UNDELIVERABLE: "undeliverable",
  FRAUD_SUSPECTED: "fraud_suspected"
};
var cancellationReasonSchema = z.enum(CancellationReason).openapi("CancellationReason");
var orderNumberSchema = z.string().trim().min(4).max(32).regex(/^[A-Z0-9-]+$/).openapi("OrderNumber");
var orderAddressSchema = z.object({
  fullName: z.string(),
  phone: phoneSchema,
  line1: z.string(),
  line2: z.string().nullable(),
  landmark: z.string().nullable(),
  city: z.string(),
  state: z.string(),
  pincode: pincodeSchema,
  country: countryCodeSchema
}).openapi("OrderAddress");
var orderItemSchema = z.object({
  id: uuidSchema,
  productId: uuidSchema,
  title: z.string(),
  slug: z.string(),
  sku: z.string(),
  imageUrl: urlSchema.nullable(),
  brandName: z.string().nullable(),
  oemNumber: z.string().nullable(),
  condition: productConditionSchema,
  grade: productGradeSchema.nullable(),
  quantity: z.number().int().positive(),
  unitPrice: moneySchema,
  mrp: moneySchema.nullable(),
  discount: moneySchema,
  taxRate: percentSchema,
  taxAmount: moneySchema,
  lineTotal: moneySchema,
  warrantyDays: z.number().int().min(0).nullable()
}).openapi("OrderItem");
var orderTotalsSchema = z.object({
  subtotal: moneySchema,
  discount: moneySchema,
  taxTotal: moneySchema,
  shippingTotal: moneySchema,
  grandTotal: moneySchema,
  refundedTotal: moneySchema,
  currency: currencyCodeSchema
}).openapi("OrderTotals");
var orderStatusHistoryEntrySchema = z.object({
  id: uuidSchema,
  fromStatus: orderStatusSchema.nullable(),
  toStatus: orderStatusSchema,
  note: z.string().nullable(),
  actor: actorSchema.nullable(),
  createdAt: timestampSchema
}).openapi("OrderStatusHistoryEntry");
var orderCustomerSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  email: z.string(),
  phone: phoneSchema.nullable()
}).openapi("OrderCustomer");
var orderListItemSchema = z.object({
  id: uuidSchema,
  orderNumber: orderNumberSchema,
  status: orderStatusSchema,
  paymentStatus: paymentStatusSchema,
  customer: orderCustomerSchema,
  itemCount: z.number().int().positive(),
  grandTotal: moneySchema,
  currency: currencyCodeSchema,
  placedAt: timestampSchema,
  updatedAt: timestampSchema
}).openapi("OrderListItem");
var orderSchema = orderListItemSchema.extend({
  items: z.array(orderItemSchema),
  totals: orderTotalsSchema,
  taxBreakdown: z.array(taxLineSchema),
  coupon: couponAppliedSchema.nullable(),
  shippingAddress: orderAddressSchema,
  billingAddress: orderAddressSchema,
  payments: z.array(paymentSchema),
  refunds: z.array(refundSchema),
  shipments: z.array(shipmentSchema),
  statusHistory: z.array(orderStatusHistoryEntrySchema),
  notes: z.string().nullable(),
  internalNotes: z.string().nullable(),
  cancelledAt: timestampSchema.nullable(),
  cancellationReason: cancellationReasonSchema.nullable(),
  deliveredAt: timestampSchema.nullable()
}).openapi("Order");
var orderItemCreateSchema = z.object({ productId: uuidSchema, quantity: z.number().int().positive().max(99) }).openapi("OrderItemCreate");
var orderCreateSchema = z.object({
  userId: uuidSchema,
  items: z.array(orderItemCreateSchema).min(1).max(100),
  shippingAddressId: uuidSchema,
  billingAddressId: uuidSchema,
  couponCode: z.string().trim().max(32).nullable().optional(),
  notes: z.string().trim().max(500).nullable().optional(),
  idempotencyKey: idempotencyKeySchema
}).openapi("OrderCreate");
var orderUpdateSchema = z.object({
  notes: z.string().trim().max(500).nullable(),
  internalNotes: z.string().trim().max(2e3).nullable(),
  shippingAddress: orderAddressSchema
}).partial().openapi("OrderUpdate");
var orderSortSchema = z.enum(["placedAt", "updatedAt", "grandTotal", "orderNumber"]).openapi("OrderSort");
var orderQuerySchema = listQuerySchema.extend({
  sort: orderSortSchema.optional(),
  status: orderStatusSchema.optional(),
  statuses: z.array(orderStatusSchema).optional(),
  paymentStatus: paymentStatusSchema.optional(),
  userId: uuidSchema.optional(),
  orderNumber: orderNumberSchema.optional(),
  minTotal: moneySchema.optional(),
  maxTotal: moneySchema.optional(),
  dateFrom: timestampSchema.optional(),
  dateTo: timestampSchema.optional()
}).openapi("OrderQuery");
var orderStatusTransitionSchema = z.object({ from: orderStatusSchema, to: orderStatusSchema }).openapi("OrderStatusTransition");
var orderTransitionRequestSchema = z.object({
  status: orderStatusSchema,
  note: z.string().trim().max(500).nullable().optional(),
  notifyCustomer: z.boolean().default(true)
}).openapi("OrderTransitionRequest");
var orderCancelRequestSchema = z.object({
  reason: cancellationReasonSchema,
  note: z.string().trim().max(500).nullable().optional(),
  refund: z.boolean().default(true)
}).openapi("OrderCancelRequest");
var orderAllowedTransitionsSchema = z.object({ current: orderStatusSchema, allowed: z.array(orderStatusSchema) }).openapi("OrderAllowedTransitions");
var orderStatusCountSchema = z.object({ status: orderStatusSchema, count: z.number().int().min(0) }).openapi("OrderStatusCount");

// src/customers/index.ts
var customers_exports = {};
__export(customers_exports, {
  customerCreateSchema: () => customerCreateSchema,
  customerDetailSchema: () => customerDetailSchema,
  customerListItemSchema: () => customerListItemSchema,
  customerQuerySchema: () => customerQuerySchema,
  customerSortSchema: () => customerSortSchema,
  customerStatsSchema: () => customerStatsSchema,
  customerStatusChangeSchema: () => customerStatusChangeSchema,
  customerUpdateSchema: () => customerUpdateSchema,
  wishlistAddRequestSchema: () => wishlistAddRequestSchema,
  wishlistItemSchema: () => wishlistItemSchema,
  wishlistProductParamSchema: () => wishlistProductParamSchema,
  wishlistProductSchema: () => wishlistProductSchema,
  wishlistQuerySchema: () => wishlistQuerySchema
});
var customerListItemSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  email: emailSchema,
  phone: phoneSchema.nullable(),
  avatarUrl: urlSchema.nullable(),
  role: userRoleSchema,
  status: userStatusSchema,
  orderCount: z.number().int().min(0),
  totalSpent: moneySchema,
  lastOrderAt: timestampSchema.nullable(),
  lastLoginAt: timestampSchema.nullable(),
  isActive: z.boolean(),
  createdAt: timestampSchema
}).openapi("CustomerListItem");
var customerStatsSchema = z.object({
  orderCount: z.number().int().min(0),
  totalSpent: moneySchema,
  averageOrderValue: moneySchema,
  cancelledCount: z.number().int().min(0),
  refundedTotal: moneySchema,
  firstOrderAt: timestampSchema.nullable(),
  lastOrderAt: timestampSchema.nullable()
}).openapi("CustomerStats");
var customerDetailSchema = customerListItemSchema.extend({
  stats: customerStatsSchema,
  addresses: z.array(addressSchema),
  vehicles: z.array(userVehicleSchema),
  recentOrders: z.array(orderListItemSchema),
  notes: z.string().nullable(),
  updatedAt: timestampSchema
}).openapi("CustomerDetail");
var customerCreateSchema = z.object({
  name: z.string().trim().min(1).max(120),
  email: emailSchema,
  phone: phoneSchema.nullable().optional(),
  notes: z.string().trim().max(2e3).nullable().optional()
}).openapi("CustomerCreate");
var customerUpdateSchema = z.object({
  name: z.string().trim().min(1).max(120),
  phone: phoneSchema.nullable(),
  status: userStatusSchema,
  notes: z.string().trim().max(2e3).nullable()
}).partial().openapi("CustomerUpdate");
var customerSortSchema = z.enum(["createdAt", "name", "orderCount", "totalSpent", "lastOrderAt"]).openapi("CustomerSort");
var customerQuerySchema = listQuerySchema.extend({
  sort: customerSortSchema.optional(),
  status: userStatusSchema.optional(),
  hasOrders: booleanQuerySchema.optional(),
  minOrderCount: z.coerce.number().int().min(0).optional(),
  minTotalSpent: moneySchema.optional(),
  createdFrom: timestampSchema.optional(),
  createdTo: timestampSchema.optional()
}).openapi("CustomerQuery");
var customerStatusChangeSchema = z.object({ status: userStatusSchema, reason: z.string().trim().max(500).nullable().optional() }).openapi("CustomerStatusChange");
var wishlistProductSchema = productSummarySchema.extend({
  brandName: z.string().nullable(),
  categoryName: z.string(),
  vehicleMake: z.string().nullable(),
  vehicleModel: z.string().nullable()
});
var wishlistItemSchema = z.object({
  id: uuidSchema,
  product: wishlistProductSchema,
  createdAt: timestampSchema
}).openapi("WishlistItem");
var wishlistAddRequestSchema = z.object({ productId: uuidSchema }).openapi("WishlistAddRequest");
var wishlistQuerySchema = listQuerySchema.openapi("WishlistQuery");
var wishlistProductParamSchema = z.object({ productId: uuidSchema }).openapi("WishlistProductParam");

// src/notifications/index.ts
var notifications_exports = {};
__export(notifications_exports, {
  ADMIN_ONLY_NOTIFICATION_TYPES: () => ADMIN_ONLY_NOTIFICATION_TYPES,
  DevicePlatform: () => DevicePlatform,
  NotificationAudience: () => NotificationAudience,
  NotificationType: () => NotificationType,
  devicePlatformSchema: () => devicePlatformSchema,
  deviceTokenDeleteSchema: () => deviceTokenDeleteSchema,
  deviceTokenQuerySchema: () => deviceTokenQuerySchema,
  deviceTokenRegisterSchema: () => deviceTokenRegisterSchema,
  deviceTokenSchema: () => deviceTokenSchema,
  deviceTokenUpdateSchema: () => deviceTokenUpdateSchema,
  expoPushTokenSchema: () => expoPushTokenSchema,
  notificationAudienceSchema: () => notificationAudienceSchema,
  notificationClearResultSchema: () => notificationClearResultSchema,
  notificationCreateSchema: () => notificationCreateSchema,
  notificationDispatchResultSchema: () => notificationDispatchResultSchema,
  notificationMarkReadSchema: () => notificationMarkReadSchema,
  notificationQuerySchema: () => notificationQuerySchema,
  notificationSchema: () => notificationSchema,
  notificationSendHistoryItemSchema: () => notificationSendHistoryItemSchema,
  notificationSendHistoryQuerySchema: () => notificationSendHistoryQuerySchema,
  notificationTypeSchema: () => notificationTypeSchema,
  notificationUpdateSchema: () => notificationUpdateSchema,
  unreadCountQuerySchema: () => unreadCountQuerySchema,
  unreadCountSchema: () => unreadCountSchema,
  webPushKeysSchema: () => webPushKeysSchema,
  webPushPublicKeySchema: () => webPushPublicKeySchema,
  webPushSubscribeSchema: () => webPushSubscribeSchema,
  webPushUnsubscribeSchema: () => webPushUnsubscribeSchema
});
var DevicePlatform = { IOS: "ios", ANDROID: "android", WEB: "web" };
var devicePlatformSchema = z.enum(DevicePlatform).openapi("DevicePlatform");
var NotificationType = {
  ORDER_UPDATE: "order_update",
  PAYMENT: "payment",
  SHIPMENT: "shipment",
  PROMOTION: "promotion",
  SYSTEM: "system",
  LOW_STOCK: "low_stock",
  ADMIN_INVITE: "admin_invite",
  CART_WISHLIST_REMINDER: "cart_wishlist_reminder"
};
var ADMIN_ONLY_NOTIFICATION_TYPES = [
  NotificationType.LOW_STOCK,
  NotificationType.ADMIN_INVITE
];
var notificationTypeSchema = z.enum(NotificationType).openapi("NotificationType");
var NotificationAudience = {
  ALL: "all",
  CUSTOMERS: "customers",
  ADMINS: "admins",
  SELECTED: "selected"
};
var notificationAudienceSchema = z.enum(NotificationAudience).openapi("NotificationAudience");
var expoPushTokenSchema = z.string().trim().min(10).max(256).openapi("ExpoPushToken");
var deviceTokenRegisterSchema = z.object({
  token: expoPushTokenSchema,
  platform: devicePlatformSchema,
  deviceId: z.string().trim().min(1).max(128),
  appVersion: z.string().trim().max(32).optional()
}).openapi("DeviceTokenRegister");
var deviceTokenSchema = z.object({
  id: uuidSchema,
  userId: uuidSchema,
  token: expoPushTokenSchema,
  platform: devicePlatformSchema,
  deviceId: z.string(),
  appVersion: z.string().nullable(),
  lastSeenAt: timestampSchema,
  createdAt: timestampSchema
}).openapi("DeviceToken");
var deviceTokenUpdateSchema = z.object({ appVersion: z.string().trim().max(32), lastSeenAt: timestampSchema }).partial().openapi("DeviceTokenUpdate");
var deviceTokenQuerySchema = listQuerySchema.extend({ userId: uuidSchema.optional(), platform: devicePlatformSchema.optional() }).openapi("DeviceTokenQuery");
var deviceTokenDeleteSchema = z.object({ token: expoPushTokenSchema }).openapi("DeviceTokenDelete");
var webPushKeysSchema = z.object({
  p256dh: z.string().trim().min(1).max(255),
  auth: z.string().trim().min(1).max(255)
}).openapi("WebPushKeys");
var webPushSubscribeSchema = z.object({
  endpoint: urlSchema.max(1024),
  keys: webPushKeysSchema,
  userAgent: z.string().trim().max(255).optional()
}).openapi("WebPushSubscribe");
var webPushUnsubscribeSchema = z.object({ endpoint: urlSchema.max(1024) }).openapi("WebPushUnsubscribe");
var webPushPublicKeySchema = z.object({ publicKey: z.string().min(1).nullable() }).openapi("WebPushPublicKey");
var notificationSchema = z.object({
  id: uuidSchema,
  userId: uuidSchema,
  type: notificationTypeSchema,
  title: z.string(),
  body: z.string(),
  imageUrl: urlSchema.nullable(),
  data: z.record(z.string(), z.unknown()),
  readAt: timestampSchema.nullable(),
  createdAt: timestampSchema
}).openapi("Notification");
var notificationCreateSchema = z.object({
  type: notificationTypeSchema,
  title: z.string().trim().min(1).max(120),
  body: z.string().trim().min(1).max(500),
  imageUrl: urlSchema.nullable().optional(),
  data: z.record(z.string(), z.unknown()).default({}),
  audience: notificationAudienceSchema.default("selected"),
  userIds: z.array(uuidSchema).max(1e3).default([]),
  push: z.boolean().default(true),
  scheduledAt: timestampSchema.nullable().optional()
}).refine((v) => v.audience !== "selected" || v.userIds.length > 0, {
  path: ["userIds"],
  message: "userIds is required when the audience is selected"
}).openapi("NotificationCreate");
var notificationUpdateSchema = z.object({
  title: z.string().trim().min(1).max(120),
  body: z.string().trim().min(1).max(500),
  imageUrl: urlSchema.nullable(),
  scheduledAt: timestampSchema.nullable()
}).partial().openapi("NotificationUpdate");
var notificationQuerySchema = listQuerySchema.extend({
  type: notificationTypeSchema.optional(),
  unreadOnly: booleanQuerySchema.optional(),
  excludeAdminTypes: booleanQuerySchema.optional(),
  userId: uuidSchema.optional(),
  dateFrom: timestampSchema.optional(),
  dateTo: timestampSchema.optional()
}).openapi("NotificationQuery");
var notificationSendHistoryQuerySchema = listQuerySchema.openapi(
  "NotificationSendHistoryQuery"
);
var notificationSendHistoryItemSchema = z.object({
  id: uuidSchema,
  sentBy: z.object({ id: uuidSchema, name: z.string() }).nullable(),
  type: notificationTypeSchema,
  title: z.string(),
  body: z.string(),
  audience: notificationAudienceSchema,
  scheduledAt: timestampSchema.nullable(),
  createdAt: timestampSchema,
  totalRecipients: z.number().int().min(0),
  readCount: z.number().int().min(0),
  unreadCount: z.number().int().min(0)
}).openapi("NotificationSendHistoryItem");
var notificationMarkReadSchema = z.object({ ids: z.array(uuidSchema).max(200).default([]), all: z.boolean().default(false) }).refine((v) => v.all || v.ids.length > 0, {
  path: ["ids"],
  message: "provide ids or set all to true"
}).openapi("NotificationMarkRead");
var unreadCountQuerySchema = z.object({ excludeAdminTypes: booleanQuerySchema.optional() }).openapi("UnreadCountQuery");
var unreadCountSchema = z.object({ unread: z.number().int().min(0) }).openapi("UnreadCount");
var notificationClearResultSchema = z.object({ deleted: z.number().int().min(0) }).openapi("NotificationClearResult");
var notificationDispatchResultSchema = z.object({
  created: z.number().int().min(0),
  pushed: z.number().int().min(0),
  failed: z.number().int().min(0)
}).openapi("NotificationDispatchResult");

// src/reports/index.ts
var reports_exports = {};
__export(reports_exports, {
  ReportGroupBy: () => ReportGroupBy,
  ReportRange: () => ReportRange,
  customerReportQuerySchema: () => customerReportQuerySchema,
  customerReportRowSchema: () => customerReportRowSchema,
  dashboardMetricsSchema: () => dashboardMetricsSchema,
  dashboardQuerySchema: () => dashboardQuerySchema,
  dashboardSummarySchema: () => dashboardSummarySchema,
  lowStockRowSchema: () => lowStockRowSchema,
  metricDeltaSchema: () => metricDeltaSchema,
  ordersByStatusRowSchema: () => ordersByStatusRowSchema,
  productPerformanceQuerySchema: () => productPerformanceQuerySchema,
  productPerformanceRowSchema: () => productPerformanceRowSchema,
  reportGroupBySchema: () => reportGroupBySchema,
  reportPeriodSchema: () => reportPeriodSchema,
  reportRangeSchema: () => reportRangeSchema,
  salesReportQuerySchema: () => salesReportQuerySchema,
  salesReportRowSchema: () => salesReportRowSchema,
  salesReportSchema: () => salesReportSchema,
  timeSeriesPointSchema: () => timeSeriesPointSchema,
  topCategoryRowSchema: () => topCategoryRowSchema,
  topProductRowSchema: () => topProductRowSchema,
  wishlistProductRowSchema: () => wishlistProductRowSchema,
  wishlistReportQuerySchema: () => wishlistReportQuerySchema
});
var ReportRange = {
  TODAY: "today",
  LAST_7_DAYS: "7d",
  LAST_30_DAYS: "30d",
  LAST_90_DAYS: "90d",
  YEAR_TO_DATE: "ytd",
  CUSTOM: "custom"
};
var reportRangeSchema = z.enum(ReportRange).openapi("ReportRange");
var ReportGroupBy = { DAY: "day", WEEK: "week", MONTH: "month" };
var reportGroupBySchema = z.enum(ReportGroupBy).openapi("ReportGroupBy");
var reportPeriodSchema = z.object({ from: timestampSchema, to: timestampSchema, label: z.string() }).openapi("ReportPeriod");
var metricDeltaSchema = z.object({
  value: signedMoneySchema,
  percent: z.number().nullable(),
  direction: z.enum(["up", "down", "flat"])
}).openapi("MetricDelta");
var timeSeriesPointSchema = z.object({ bucket: dateOnlySchema, value: z.number() }).openapi("TimeSeriesPoint");
var dashboardQuerySchema = z.object({
  range: reportRangeSchema.default("30d"),
  dateFrom: timestampSchema.optional(),
  dateTo: timestampSchema.optional(),
  compare: booleanQuerySchema.default(false)
}).refine((v) => v.range !== "custom" || v.dateFrom != null && v.dateTo != null, {
  path: ["dateFrom"],
  message: "dateFrom and dateTo are required for a custom range"
}).openapi("DashboardQuery");
var dashboardMetricsSchema = z.object({
  revenue: moneySchema,
  netRevenue: moneySchema,
  orders: z.number().int().min(0),
  unitsSold: z.number().int().min(0),
  averageOrderValue: moneySchema,
  newCustomers: z.number().int().min(0),
  refunds: moneySchema,
  cancelledOrders: z.number().int().min(0),
  conversionRate: z.number().min(0).max(100).nullable()
}).openapi("DashboardMetrics");
var topProductRowSchema = z.object({
  product: productSummarySchema,
  unitsSold: z.number().int().min(0),
  revenue: moneySchema
}).openapi("TopProductRow");
var topCategoryRowSchema = z.object({
  categoryId: uuidSchema,
  name: z.string(),
  unitsSold: z.number().int().min(0),
  revenue: moneySchema
}).openapi("TopCategoryRow");
var ordersByStatusRowSchema = z.object({ status: orderStatusSchema, count: z.number().int().min(0), value: moneySchema }).openapi("OrdersByStatusRow");
var lowStockRowSchema = z.object({
  product: productSummarySchema,
  available: z.number().int().min(0),
  reorderLevel: z.number().int().min(0)
}).openapi("LowStockRow");
var dashboardSummarySchema = z.object({
  period: reportPeriodSchema,
  comparisonPeriod: reportPeriodSchema.nullable(),
  currency: currencyCodeSchema,
  metrics: dashboardMetricsSchema,
  deltas: z.record(z.string(), metricDeltaSchema).nullable(),
  revenueSeries: z.array(timeSeriesPointSchema),
  ordersSeries: z.array(timeSeriesPointSchema),
  ordersByStatus: z.array(ordersByStatusRowSchema),
  topProducts: z.array(topProductRowSchema),
  topCategories: z.array(topCategoryRowSchema),
  lowStock: z.array(lowStockRowSchema)
}).openapi("DashboardSummary");
var salesReportQuerySchema = listQuerySchema.extend({
  range: reportRangeSchema.default("30d"),
  dateFrom: timestampSchema.optional(),
  dateTo: timestampSchema.optional(),
  groupBy: reportGroupBySchema.default("day"),
  categoryId: uuidSchema.optional(),
  brandId: uuidSchema.optional(),
  status: orderStatusSchema.optional()
}).openapi("SalesReportQuery");
var salesReportRowSchema = z.object({
  bucket: dateOnlySchema,
  orders: z.number().int().min(0),
  unitsSold: z.number().int().min(0),
  gross: moneySchema,
  discount: moneySchema,
  tax: moneySchema,
  shipping: moneySchema,
  refunds: moneySchema,
  net: moneySchema
}).openapi("SalesReportRow");
var salesReportSchema = z.object({
  period: reportPeriodSchema,
  currency: currencyCodeSchema,
  groupBy: reportGroupBySchema,
  rows: z.array(salesReportRowSchema),
  totals: salesReportRowSchema.omit({ bucket: true })
}).openapi("SalesReport");
var productPerformanceQuerySchema = listQuerySchema.extend({
  range: reportRangeSchema.default("30d"),
  dateFrom: timestampSchema.optional(),
  dateTo: timestampSchema.optional(),
  categoryId: uuidSchema.optional(),
  brandId: uuidSchema.optional()
}).openapi("ProductPerformanceQuery");
var productPerformanceRowSchema = z.object({
  product: productSummarySchema,
  unitsSold: z.number().int().min(0),
  revenue: moneySchema,
  refunds: moneySchema,
  returnRate: z.number().min(0).max(100)
}).openapi("ProductPerformanceRow");
var customerReportQuerySchema = listQuerySchema.extend({
  range: reportRangeSchema.default("30d"),
  dateFrom: timestampSchema.optional(),
  dateTo: timestampSchema.optional(),
  minOrders: z.coerce.number().int().min(1).optional()
}).openapi("CustomerReportQuery");
var customerReportRowSchema = z.object({
  userId: uuidSchema,
  name: z.string(),
  email: z.string(),
  orders: z.number().int().min(0),
  revenue: moneySchema,
  lastOrderAt: timestampSchema.nullable()
}).openapi("CustomerReportRow");
var wishlistProductRowSchema = z.object({
  productId: uuidSchema,
  productTitle: z.string(),
  wishlistCount: z.number().int().min(0)
}).openapi("WishlistProductRow");
var wishlistReportQuerySchema = listQuerySchema.extend({ range: reportRangeSchema.default("30d") }).openapi("WishlistReportQuery");

// src/settings/index.ts
var settings_exports = {};
__export(settings_exports, {
  CmsPageSlug: () => CmsPageSlug,
  SettingKey: () => SettingKey,
  brandThemeSettingSchema: () => brandThemeSettingSchema,
  businessHoursSettingSchema: () => businessHoursSettingSchema,
  checkoutSettingSchema: () => checkoutSettingSchema,
  cmsPageSchema: () => cmsPageSchema,
  cmsPageSlugSchema: () => cmsPageSlugSchema,
  cmsPagesSettingSchema: () => cmsPagesSettingSchema,
  currencySettingSchema: () => currencySettingSchema,
  enabledVehicleTypesSettingSchema: () => enabledVehicleTypesSettingSchema,
  lowStockReminderConfigSchema: () => lowStockReminderConfigSchema,
  notificationsSettingSchema: () => notificationsSettingSchema,
  pincodeAllowlistSettingSchema: () => pincodeAllowlistSettingSchema,
  publicSettingsSchema: () => publicSettingsSchema,
  settingEntrySchema: () => settingEntrySchema,
  settingKeyParamSchema: () => settingKeyParamSchema,
  settingKeySchema: () => settingKeySchema,
  settingSchema: () => settingSchema,
  settingUpdateSchema: () => settingUpdateSchema,
  settingValueSchemas: () => settingValueSchemas,
  settingsBulkUpdateSchema: () => settingsBulkUpdateSchema,
  settingsQuerySchema: () => settingsQuerySchema,
  shippingBandsSettingSchema: () => shippingBandsSettingSchema,
  storeProfileSettingSchema: () => storeProfileSettingSchema,
  taxDefaultsSettingSchema: () => taxDefaultsSettingSchema
});
var SettingKey = {
  STORE_PROFILE: "store_profile",
  BRAND_THEME: "brand_theme",
  CURRENCY: "currency",
  TAX_DEFAULTS: "tax_defaults",
  PINCODE_ALLOWLIST: "pincode_allowlist",
  SHIPPING_BANDS: "shipping_bands",
  BUSINESS_HOURS: "business_hours",
  ENABLED_VEHICLE_TYPES: "enabled_vehicle_types",
  CHECKOUT: "checkout",
  NOTIFICATIONS: "notifications",
  CMS_PAGES: "cms_pages"
};
var settingKeySchema = z.enum(SettingKey).openapi("SettingKey");
var storeProfileSettingSchema = z.object({
  name: z.string().trim().min(1).max(120),
  tagline: z.string().trim().max(200).nullable(),
  logoUrl: urlSchema.nullable(),
  faviconUrl: urlSchema.nullable(),
  supportEmail: emailSchema,
  supportPhone: phoneSchema.nullable(),
  supportWhatsapp: phoneSchema.nullable().optional(),
  addressLines: z.array(z.string().trim().max(200)).max(5),
  gstin: z.string().trim().max(20).nullable()
}).openapi("StoreProfileSetting");
var brandThemeSettingSchema = z.object({
  primary: z.string().regex(/^#[0-9a-fA-F]{6}$/),
  secondary: z.string().regex(/^#[0-9a-fA-F]{6}$/),
  accent: z.string().regex(/^#[0-9a-fA-F]{6}$/),
  logoUrl: urlSchema.nullable(),
  defaultMode: z.enum(["light", "dark"])
}).openapi("BrandThemeSetting");
var currencySettingSchema = z.object({
  code: currencyCodeSchema,
  symbol: z.string().trim().min(1).max(4),
  locale: z.string().trim().min(2).max(16)
}).openapi("CurrencySetting");
var taxDefaultsSettingSchema = z.object({
  defaultRate: percentSchema,
  pricesIncludeTax: z.boolean(),
  label: z.string().trim().min(1).max(20)
}).openapi("TaxDefaultsSetting");
var pincodeAllowlistSettingSchema = z.object({ enabled: z.boolean(), pincodes: z.array(pincodeSchema).max(5e3) }).openapi("PincodeAllowlistSetting");
var shippingBandsSettingSchema = z.object({
  freeShippingAbove: moneySchema.nullable(),
  bands: z.array(
    z.object({
      label: z.string().trim().min(1).max(80),
      minGrams: z.number().int().min(0),
      maxGrams: z.number().int().positive().nullable(),
      charge: moneySchema,
      etaDays: z.number().int().positive().nullable()
    })
  ).min(1)
}).openapi("ShippingBandsSetting");
var businessHoursSettingSchema = z.object({
  timezone: z.string().trim().min(1).max(64),
  days: z.array(
    z.object({
      day: z.enum(["mon", "tue", "wed", "thu", "fri", "sat", "sun"]),
      open: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
      close: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
      closed: z.boolean()
    })
  )
}).openapi("BusinessHoursSetting");
var enabledVehicleTypesSettingSchema = z.array(slugSchema).min(1).openapi("EnabledVehicleTypesSetting");
var checkoutSettingSchema = z.object({
  minimumOrderValue: moneySchema.nullable(),
  codEnabled: z.boolean(),
  codMaxOrderValue: moneySchema.nullable(),
  guestCheckoutEnabled: z.boolean(),
  fitmentWarningBlocks: z.boolean()
}).openapi("CheckoutSetting");
var notificationsSettingSchema = z.object({
  pushEnabled: z.boolean(),
  emailEnabled: z.boolean(),
  orderUpdates: z.boolean(),
  promotions: z.boolean(),
  fromEmail: emailSchema.nullable()
}).openapi("NotificationsSetting");
var CmsPageSlug = {
  PRIVACY: "privacy",
  TERMS: "terms",
  ABOUT: "about",
  CONTACT: "contact"
};
var cmsPageSlugSchema = z.enum(CmsPageSlug).openapi("CmsPageSlug");
var cmsPageSchema = z.object({
  slug: cmsPageSlugSchema,
  title: z.string().trim().min(1).max(120),
  body: z.string().max(5e4)
}).openapi("CmsPage");
var cmsPagesSettingSchema = z.object({ pages: z.array(cmsPageSchema).max(20) }).openapi("CmsPagesSetting");
var settingValueSchemas = {
  [SettingKey.STORE_PROFILE]: storeProfileSettingSchema,
  [SettingKey.BRAND_THEME]: brandThemeSettingSchema,
  [SettingKey.CURRENCY]: currencySettingSchema,
  [SettingKey.TAX_DEFAULTS]: taxDefaultsSettingSchema,
  [SettingKey.PINCODE_ALLOWLIST]: pincodeAllowlistSettingSchema,
  [SettingKey.SHIPPING_BANDS]: shippingBandsSettingSchema,
  [SettingKey.BUSINESS_HOURS]: businessHoursSettingSchema,
  [SettingKey.ENABLED_VEHICLE_TYPES]: enabledVehicleTypesSettingSchema,
  [SettingKey.CHECKOUT]: checkoutSettingSchema,
  [SettingKey.NOTIFICATIONS]: notificationsSettingSchema,
  [SettingKey.CMS_PAGES]: cmsPagesSettingSchema
};
var settingEntrySchema = z.discriminatedUnion("key", [
  z.object({ key: z.literal(SettingKey.STORE_PROFILE), value: storeProfileSettingSchema }),
  z.object({ key: z.literal(SettingKey.BRAND_THEME), value: brandThemeSettingSchema }),
  z.object({ key: z.literal(SettingKey.CURRENCY), value: currencySettingSchema }),
  z.object({ key: z.literal(SettingKey.TAX_DEFAULTS), value: taxDefaultsSettingSchema }),
  z.object({
    key: z.literal(SettingKey.PINCODE_ALLOWLIST),
    value: pincodeAllowlistSettingSchema
  }),
  z.object({ key: z.literal(SettingKey.SHIPPING_BANDS), value: shippingBandsSettingSchema }),
  z.object({ key: z.literal(SettingKey.BUSINESS_HOURS), value: businessHoursSettingSchema }),
  z.object({
    key: z.literal(SettingKey.ENABLED_VEHICLE_TYPES),
    value: enabledVehicleTypesSettingSchema
  }),
  z.object({ key: z.literal(SettingKey.CHECKOUT), value: checkoutSettingSchema }),
  z.object({ key: z.literal(SettingKey.NOTIFICATIONS), value: notificationsSettingSchema }),
  z.object({ key: z.literal(SettingKey.CMS_PAGES), value: cmsPagesSettingSchema })
]).openapi("SettingEntry");
var settingSchema = z.object({
  key: settingKeySchema,
  value: z.unknown(),
  updatedAt: timestampSchema,
  updatedBy: z.string().nullable()
}).openapi("Setting");
var settingUpdateSchema = z.object({ value: z.unknown() }).openapi("SettingUpdate");
var settingsBulkUpdateSchema = z.object({ settings: z.array(settingEntrySchema).min(1) }).openapi("SettingsBulkUpdate");
var settingKeyParamSchema = z.object({ key: settingKeySchema }).openapi("SettingKeyParam");
var settingsQuerySchema = listQuerySchema.extend({ keys: z.array(settingKeySchema).optional() }).openapi("SettingsQuery");
var publicSettingsSchema = z.object({
  store: storeProfileSettingSchema,
  theme: brandThemeSettingSchema,
  currency: currencySettingSchema,
  tax: taxDefaultsSettingSchema.omit({ defaultRate: true }),
  checkout: checkoutSettingSchema,
  enabledVehicleTypes: enabledVehicleTypesSettingSchema,
  showVehicleTypeSelector: z.boolean(),
  cms: cmsPagesSettingSchema
}).openapi("PublicSettings");
var lowStockReminderConfigSchema = z.object({
  enabled: z.boolean(),
  firstReminderTime: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
  secondReminderTime: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/)
}).openapi("LowStockReminderConfig");

// src/admin-users/index.ts
var admin_users_exports = {};
__export(admin_users_exports, {
  AdminInviteStatus: () => AdminInviteStatus,
  AdminRole: () => AdminRole,
  adminInviteAcceptSchema: () => adminInviteAcceptSchema,
  adminInviteCreateSchema: () => adminInviteCreateSchema,
  adminInvitePreviewSchema: () => adminInvitePreviewSchema,
  adminInviteQuerySchema: () => adminInviteQuerySchema,
  adminInviteSchema: () => adminInviteSchema,
  adminInviteStatusSchema: () => adminInviteStatusSchema,
  adminInviteUpdateSchema: () => adminInviteUpdateSchema,
  adminPermissionRoleCreateSchema: () => adminPermissionRoleCreateSchema,
  adminPermissionRoleSchema: () => adminPermissionRoleSchema,
  adminPermissionRoleUpdateSchema: () => adminPermissionRoleUpdateSchema,
  adminRoleChangeSchema: () => adminRoleChangeSchema,
  adminRoleSchema: () => adminRoleSchema,
  adminUserQuerySchema: () => adminUserQuerySchema,
  adminUserSchema: () => adminUserSchema,
  adminUserUpdateSchema: () => adminUserUpdateSchema,
  auditLogQuerySchema: () => auditLogQuerySchema,
  auditLogSchema: () => auditLogSchema,
  inviteTokenSchema: () => inviteTokenSchema
});
var AdminRole = { ADMIN: "admin", SUPER_ADMIN: "super_admin" };
var adminRoleSchema = z.enum(AdminRole).openapi("AdminRole");
var AdminInviteStatus = {
  PENDING: "pending",
  ACCEPTED: "accepted",
  EXPIRED: "expired",
  REVOKED: "revoked"
};
var adminInviteStatusSchema = z.enum(AdminInviteStatus).openapi("AdminInviteStatus");
var inviteTokenSchema = z.string().trim().min(16).max(256).openapi("InviteToken");
var adminInviteSchema = z.object({
  id: uuidSchema,
  email: emailSchema,
  role: adminRoleSchema,
  status: adminInviteStatusSchema,
  invitedById: uuidSchema,
  invitedByName: z.string(),
  expiresAt: timestampSchema,
  acceptedAt: timestampSchema.nullable(),
  revokedAt: timestampSchema.nullable(),
  createdAt: timestampSchema
}).openapi("AdminInvite");
var adminInviteCreateSchema = z.object({ email: emailSchema, role: adminRoleSchema.default("admin") }).openapi("AdminInviteCreate");
var adminInviteUpdateSchema = z.object({ role: adminRoleSchema, expiresAt: timestampSchema }).partial().openapi("AdminInviteUpdate");
var adminInviteQuerySchema = listQuerySchema.extend({ status: adminInviteStatusSchema.optional(), role: adminRoleSchema.optional() }).openapi("AdminInviteQuery");
var adminInviteAcceptSchema = z.object({
  token: inviteTokenSchema,
  idToken: z.string().min(1),
  name: z.string().trim().min(1).max(120).optional()
}).openapi("AdminInviteAccept");
var adminInvitePreviewSchema = z.object({
  email: emailSchema,
  role: adminRoleSchema,
  storeName: z.string(),
  invitedByName: z.string(),
  expiresAt: timestampSchema,
  valid: z.boolean(),
  reason: adminInviteStatusSchema.nullable()
}).openapi("AdminInvitePreview");
var adminUserSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  email: emailSchema,
  avatarUrl: urlSchema.nullable(),
  role: adminRoleSchema,
  status: userStatusSchema,
  permissions: z.array(permissionKeySchema),
  lastLoginAt: timestampSchema.nullable(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema
}).openapi("AdminUser");
var adminUserUpdateSchema = z.object({
  role: adminRoleSchema,
  status: userStatusSchema,
  permissions: z.array(permissionKeySchema).optional(),
  rejectionReason: z.string().trim().max(500).nullable().optional()
}).partial().openapi("AdminUserUpdate");
var adminUserQuerySchema = listQuerySchema.extend({ role: adminRoleSchema.optional(), status: userStatusSchema.optional() }).openapi("AdminUserQuery");
var adminRoleChangeSchema = z.object({ role: adminRoleSchema, reason: z.string().trim().max(500).nullable().optional() }).openapi("AdminRoleChange");
var auditLogSchema = z.object({
  id: uuidSchema,
  actorId: uuidSchema.nullable(),
  actorName: z.string().nullable(),
  action: z.string(),
  entity: z.string(),
  entityId: z.string().nullable(),
  before: z.record(z.string(), z.unknown()).nullable(),
  after: z.record(z.string(), z.unknown()).nullable(),
  requestId: z.string().nullable(),
  createdAt: timestampSchema
}).openapi("AuditLog");
var auditLogQuerySchema = listQuerySchema.extend({
  actorId: uuidSchema.optional(),
  action: z.string().trim().max(80).optional(),
  entity: z.string().trim().max(80).optional(),
  entityId: z.string().trim().max(64).optional(),
  dateFrom: timestampSchema.optional(),
  dateTo: timestampSchema.optional()
}).openapi("AuditLogQuery");
var adminPermissionRoleSchema = z.object({
  id: uuidSchema,
  name: z.string().trim().min(1).max(80),
  description: z.string().trim().max(500).nullable(),
  permissions: z.array(permissionKeySchema),
  createdAt: timestampSchema,
  updatedAt: timestampSchema
}).openapi("AdminPermissionRole");
var adminPermissionRoleCreateSchema = z.object({
  name: z.string().trim().min(1).max(80),
  description: z.string().trim().max(500).nullable().optional(),
  permissions: z.array(permissionKeySchema).min(1)
}).openapi("AdminPermissionRoleCreate");
var adminPermissionRoleUpdateSchema = adminPermissionRoleCreateSchema.partial().openapi("AdminPermissionRoleUpdate");

// src/feedback/index.ts
var feedback_exports = {};
__export(feedback_exports, {
  feedbackCreateSchema: () => feedbackCreateSchema,
  feedbackQuerySchema: () => feedbackQuerySchema,
  feedbackSchema: () => feedbackSchema
});
var feedbackSchema = z.object({
  id: uuidSchema,
  userId: uuidSchema,
  userName: z.string(),
  message: z.string(),
  createdAt: timestampSchema
}).openapi("Feedback");
var feedbackQuerySchema = listQuerySchema.openapi("FeedbackQuery");
var feedbackCreateSchema = z.object({ message: z.string().trim().min(1).max(2e3) }).openapi("FeedbackCreate");

export { ErrorCode, MAX_PAGE_SIZE, actorSchema, admin_users_exports as adminUsers, auth_exports as auth, booleanQuerySchema, bulkIdsSchema, cart_exports as cart, catalog_exports as catalog, checkout_exports as checkout, common_exports as common, countResponseSchema, countryCodeSchema, currencyCodeSchema, customers_exports as customers, dateOnlySchema, dateRangeQuerySchema, emailSchema, errorBodySchema, errorCodeSchema, errorDetailSchema, errorResponseSchema, feedback_exports as feedback, idParamSchema, idSchema, idempotencyKeySchema, inventory_exports as inventory, listQuerySchema, media_exports as media, moneySchema, notifications_exports as notifications, okEnvelope, orders_exports as orders, paginatedEnvelope, paginationMetaSchema, paginationQuerySchema, payments_exports as payments, percentSchema, phoneSchema, pincodeSchema, promotions_exports as promotions, quantitySchema, reports_exports as reports, requestIdSchema, searchQuerySchema, settings_exports as settings, shipping_exports as shipping, signedMoneySchema, slugParamSchema, slugSchema, sortOrderSchema, sortQuerySchema, successResponseSchema, timestampSchema, urlSchema, uuidSchema, vehicles_exports as vehicles };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map